import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import LinkExistingFranchiseeModal from "@/components/contacts/LinkExistingFranchiseeModal";
import MergeContactsModal from "@/components/contacts/MergeContactsModal";
import DuplicatesModal from "@/components/contacts/DuplicatesModal";
import ContactPostcodeMapModal from "@/components/contacts/ContactPostcodeMapModal";
import { Search, AlertCircle, Kanban, X, Mail, Phone, MapPin, Calendar, Trash2, ArrowUpCircle, ArrowDownCircle, Loader2, Users, Briefcase, ArrowRightLeft, ChevronDown, ChevronUp, ChevronsLeft, ChevronsRight, CheckSquare, Square, Instagram, Facebook, Twitter, Globe, HelpCircle, UserPlus, Plus, Sparkles, Upload, FileText, CheckCircle2, Send, Award, Target, Link2, GitMerge, Home, Package, Flame, Clock, Pencil, RefreshCw, MailX } from "lucide-react";
import ReplyWithTemplateModal from "@/components/ReplyWithTemplateModal";
import EmailTimeline from "@/components/EmailTimeline";
import TerritoryMap from "@/components/territory/TerritoryMap";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import LeadTemperatureBadge from "@/components/LeadTemperatureBadge";
import SalesPipelineTabsView from "@/components/pipeline/SalesPipelineTabsView";
import BackfillHealthBanner from "@/components/BackfillHealthBanner";
import { Rows3 } from "lucide-react";

const STAGES = [
  { key: "new", label: "New", color: "bg-stone-100 text-stone-700 border-stone-300", barColor: "bg-stone-400" },
  { key: "contacted", label: "Contacted", color: "bg-blue-50 text-blue-700 border-blue-200", barColor: "bg-blue-400" },
  { key: "follow_up_due", label: "Follow-up Due", color: "bg-amber-50 text-amber-800 border-amber-300", barColor: "bg-amber-500" },
  { key: "qualified", label: "Interested", color: "bg-amber-50 text-amber-800 border-amber-200", barColor: "bg-amber-400" },
  { key: "dormant", label: "Dormant", color: "bg-orange-50 text-orange-800 border-orange-200", barColor: "bg-orange-400" },
  { key: "lost", label: "Lost", color: "bg-red-50 text-red-700 border-red-200", barColor: "bg-red-400" },
];

// "Shadow Day Booked" and "Territory Map" are no longer top-level pipeline
// stages — the equivalent steps live on the per-contact Interested-stage
// checklist (territory_defined, contract_sent, shadow_day_booked) so the
// admin can track them as small ticks rather than full stage transitions.
// FRANCHISE_ONLY_STAGES is kept as an empty Set for backwards-compat in
// any helper that still references it.
const FRANCHISE_ONLY_STAGES = new Set();

// Legacy stages we still need to *recognise* (e.g. a contact moved to
// "demo_booked" before this restructure) so we don't break their badge
// rendering. Anyone landing in these now is auto-shown the "Interested"
// stage label in the drawer.
const LEGACY_STAGE_FALLBACK = { demo_booked: "qualified", converted: "qualified" };

function stagesForContact(contact) {
  if (!contact) return STAGES;
  return STAGES;
}

const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.key, s]));

// -------------------------------------------------------------------
// Shadow-day confirmation mailto: draft.
//
// Renders inside the InterestedChecklist once BOTH the shadow date and
// the shadow host are saved. Clicking the button composes the email
// entirely client-side (no send API is called) and opens it in the
// admin's default mail handler as a DRAFT, so they can review before
// sending. Nothing else is mutated — in particular the confidentiality
// agreement's ``signed`` state is written exclusively by the public
// signing form gated by the prospect's Discovery Portal token; opening
// a mailto: draft cannot touch it.
//
// The prospect's Discovery Portal URL is a hard requirement (Sandra's
// brief: "If the prospect does not yet have a Discovery Portal link,
// show me that the link is missing instead of opening an incomplete
// email."). If it's missing we render an amber inline notice and the
// button is disabled — never fall back to a partial email.
// -------------------------------------------------------------------
function _shadowDateForUKReader(iso) {
  // Expects an ISO date (YYYY-MM-DD) as saved by the <input type="date">.
  // Produces "Tuesday 20 October 2026" per the brief.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
    timeZone: "UTC",
  });
}

function _shadowHostDisplayName(location, franchisee) {
  // Sandra's Feb-2026 refinement: keep the email casual. For HQ shadow
  // days the host is always "Sandra" (never her surname). For a
  // regional shadow day, use ONLY the franchisee's first name — falls
  // back to organisation / mojo email if a first name isn't on file
  // for some legacy reason.
  if ((location || "").toLowerCase() === "hq") {
    return "Sandra";
  }
  if ((location || "").toLowerCase() === "regional" && franchisee) {
    const first = (franchisee.first_name || "").trim();
    if (first) return first;
    return franchisee.organisation || franchisee.mojo_email || "";
  }
  return "";
}

function ShadowDayConfirmMailto({ contact, shadowDate, shadowLocation, shadowFranchisee }) {
  // ------------------------------------------------------------------
  // Behaviour (Sandra, Feb-2026 final refinement):
  //  • No automatic open on save — the earlier auto-open behaviour was
  //    awkward when correcting details. Instead we render a manual
  //    "Send Confirmation" button that only appears when every
  //    required ingredient is on file. Clicking it opens a pre-filled
  //    draft in the admin's default email app via mailto:; it never
  //    sends automatically.
  //  • If the admin changes the date or host and saves, the button's
  //    mailto: silently picks up the new values on the next render —
  //    nothing else happens until the button is clicked again.
  //  • If any ingredient is missing (prospect email, date, host, or
  //    Discovery Portal link), we render the amber inline notice
  //    instead of the button — never a partial draft.
  //  • The confidentiality-agreement signed state is written
  //    exclusively by the public signing form gated by the prospect's
  //    Discovery Portal token; a mailto: click cannot touch it.
  // ------------------------------------------------------------------
  const hostName = _shadowHostDisplayName(shadowLocation, shadowFranchisee);
  const prettyDate = _shadowDateForUKReader(shadowDate);
  const prospectEmail = (contact?.email || "").trim();
  const firstName = (contact?.first_name || "").trim() || "there";
  const slug = contact?.discovery_portal_slug || "";
  const portalUrl = slug
    ? `${window.location.origin}/discovery/${slug}`
    : "";

  const missing = [];
  if (!prospectEmail) missing.push("prospect email");
  if (!prettyDate) missing.push("shadow day date");
  if (!hostName) missing.push("shadow host");
  if (!portalUrl) missing.push("Discovery Portal link");
  const ready = missing.length === 0;

  if (!ready) {
    return (
      <div
        className="flex flex-wrap items-center gap-2 pt-1"
        data-testid="shadow-day-confirm-row"
      >
        <div
          data-testid="shadow-day-confirm-missing"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-lg"
          role="status"
        >
          <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
          Cannot open confirmation email — missing: {missing.join(", ")}.
        </div>
      </div>
    );
  }

  const subject = "Your Creative Mojo shadow day is confirmed";
  const body = [
    `Hi ${firstName},`,
    "",
    "Hope you are well.",
    "",
    `We are happy to confirm your Creative Mojo shadow day on ${prettyDate}. Your shadow day will be with ${hostName}. If you have any questions about your shadow day, including timings and arrangements, please get in touch with Sandra at sandra@creativemojo.co.uk or on 07957 343449.`,
    "",
    "Before attending, please review and sign the confidentiality agreement in your Discovery Portal:",
    portalUrl,
    "",
    "If you have any questions, please ask.",
  ].join("\n");
  const href = `mailto:${encodeURIComponent(prospectEmail)}`
    + `?subject=${encodeURIComponent(subject)}`
    + `&body=${encodeURIComponent(body)}`;

  return (
    <div
      className="flex flex-wrap items-center gap-2 pt-1"
      data-testid="shadow-day-confirm-row"
    >
      <a
        href={href}
        onClick={(e) => {
          // Hidden-anchor click via the browser handles the mailto:
          // handoff without cross-origin restrictions. We still let
          // the default anchor behaviour run — it dispatches to the
          // admin's registered mail handler.
          if (!e.defaultPrevented) return;
        }}
        data-testid="shadow-day-confirm-send"
        title={`Open a draft email to ${prospectEmail} in your default email program (does not send automatically)`}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm"
      >
        <Send className="w-3 h-3" />
        Send Confirmation
      </a>
      <span className="text-[10px] uppercase tracking-wider text-stone-500">
        Opens a draft — you review and send from your email app
      </span>
    </div>
  );
}


// Pipeline lead-temperature tag. Re-introduced May 22 2026 at the
// pipeline level: a quick read of how "warm" a lead is, displayed as a
// coloured Flame icon on the kanban card AND in the drawer header (the
// drawer copy is read-only — change it from the card to save space).
const TEMPERATURES = [
  { key: "hot",      label: "Hot",      colour: "text-orange-500", fill: "#F97316", ring: "ring-orange-300" },
  { key: "keen",     label: "Keen",     colour: "text-purple-500", fill: "#A855F7", ring: "ring-purple-300" },
  { key: "lukewarm", label: "Lukewarm", colour: "text-blue-500",   fill: "#3B82F6", ring: "ring-blue-300" },
];
const TEMP_MAP = Object.fromEntries(TEMPERATURES.map((t) => [t.key, t]));

// Small reusable picker — three coloured flames. Active flame is filled,
// the others outlined. Click on the active flame clears it. Used on
// every kanban card so Sandra can grade leads with one click.
function TemperaturePicker({ value, onChange, size = "sm", testidPrefix = "temp" }) {
  const dim = size === "xs" ? "w-3.5 h-3.5" : size === "lg" ? "w-5 h-5" : "w-4 h-4";
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={`${testidPrefix}-picker`}>
      {TEMPERATURES.map((t) => {
        const active = value === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(active ? null : t.key);
            }}
            title={`${t.label}${active ? " · click to clear" : ""}`}
            aria-label={t.label}
            data-testid={`${testidPrefix}-${t.key}${active ? "-active" : ""}`}
            className={`p-0.5 rounded-md transition-all ${active ? `bg-white ring-1 ${t.ring}` : "opacity-30 hover:opacity-100"}`}>
            <Flame
              className={`${dim} ${t.colour}`}
              fill={active ? t.fill : "none"}
              strokeWidth={active ? 1.5 : 2}
            />
          </button>
        );
      })}
    </div>
  );
}

// Read-only flame for places like the drawer header where we want to
// show the current grade without offering the picker. Returns null when
// no temperature is set.
function TemperatureFlame({ value, size = "md" }) {
  if (!value || !TEMP_MAP[value]) return null;
  const t = TEMP_MAP[value];
  const dim = size === "sm" ? "w-3.5 h-3.5" : size === "lg" ? "w-5 h-5" : "w-4 h-4";
  return (
    <span title={t.label} data-testid={`temperature-flame-${t.key}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white border border-stone-200">
      <Flame className={`${dim} ${t.colour}`} fill={t.fill} strokeWidth={1.5} />
      <span className={`text-[10px] font-bold uppercase tracking-wider ${t.colour}`}>{t.label}</span>
    </span>
  );
}

// LocalStorage-backed "Recent Searches" cache (last 10 distinct queries,
// most-recent first). Keyed per-user-agent — quick, no backend needed.
// SECURITY NOTE: this stores only the admin user's *own search terms*
// (names they've typed into the contacts filter). No PII beyond what
// they've already chosen to look up; no auth tokens, no secrets. The
// scanner's general "localStorage is unencrypted" warning doesn't apply
// to this use case.
const RECENT_SEARCH_KEY = "contactsRecentSearches";
const RECENT_SEARCH_LIMIT = 10;
function loadRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_SEARCH_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string" && s.trim()) : [];
  } catch { return []; }
}
function saveRecentSearch(q) {
  const v = (q || "").trim();
  if (!v) return;
  try {
    const cur = loadRecentSearches();
    // De-dupe case-insensitively but preserve the user's original casing.
    const lower = v.toLowerCase();
    const filtered = cur.filter((s) => s.toLowerCase() !== lower);
    const next = [v, ...filtered].slice(0, RECENT_SEARCH_LIMIT);
    localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(next));
  } catch { /* localStorage full / disabled — silently ignore */ }
}
function clearRecentSearches() {
  try { localStorage.removeItem(RECENT_SEARCH_KEY); } catch { /* noop */ }
}

const TABS = [
  { key: "pipeline", label: "Sales Pipeline", hint: "Leads being actively worked", icon: Briefcase },
  { key: "franchise", label: "Franchise Contacts", hint: "Franchise enquiries not in the pipeline", icon: Users, accent: "stone" },
  { key: "licence", label: "Licence Contacts", hint: "Licence enquiries not in the pipeline", icon: UserPlus, accent: "indigo" },
  { key: "care_home", label: "Care Home Contacts", hint: "Care-home class enquiries (reference only)", icon: Home, accent: "teal" },
  { key: "art_kit", label: "Art Kit Contacts", hint: "Deliverable Art Kit enquiries (reference only)", icon: Package, accent: "amber" },
  { key: "general", label: "General Contacts", hint: "General enquiries & legacy contacts", icon: Users },
];

// Visual differentiation for franchise vs licence enquiries throughout the app.
// Used in pipeline kanban cards (border colour), list view source pills, and the drawer.
const SOURCE_STYLE = {
  franchise_enquiry:        { label: "Franchise", pill: "bg-stone-100 text-stone-800 border-stone-300", barColor: "bg-stone-500", border: "border-l-4 border-l-stone-500" },
  licence_enquiry:          { label: "Licence",   pill: "bg-indigo-50 text-indigo-800 border-indigo-300", barColor: "bg-indigo-500", border: "border-l-4 border-l-indigo-500" },
  care_home_enquiry:        { label: "Care Home", pill: "bg-teal-50 text-teal-800 border-teal-300", barColor: "bg-teal-500", border: "border-l-4 border-l-teal-500" },
  art_kit_enquiry:          { label: "Art Kit",   pill: "bg-amber-50 text-amber-900 border-amber-300", barColor: "bg-amber-500", border: "border-l-4 border-l-amber-500" },
  general_enquiry:          { label: "General",   pill: "bg-stone-100 text-stone-700 border-stone-200", barColor: "bg-stone-400", border: "" },
  legacy_general_enquiry:   { label: "Legacy",    pill: "bg-stone-100 text-stone-500 border-stone-200", barColor: "bg-stone-300", border: "" },
};

const REFERRAL_ICONS = {
  Instagram: Instagram,
  Facebook:  Facebook,
  X:         Twitter,
  Twitter:   Twitter,
  TikTok:    Globe,
  Google:    Globe,
  Friend:    Users,
  "Word of Mouth": Users,
  Other:     HelpCircle,
};

function formatDate(value) {
  if (!value) return "—";
  // Accept "YYYY-MM-DD" or full ISO "YYYY-MM-DD HH:MM:SS" / "YYYY-MM-DDTHH:MM:SSZ"
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s.slice(0, 10);
}

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr); if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}
function daysLabel(d) {
  if (d == null) return "—";
  if (d === 0) return "today";
  if (d === 1) return "1d";
  if (d < 30) return `${d}d`;
  if (d < 365) return `${Math.floor(d / 30)}mo`;
  return `${Math.floor(d / 365)}y`;
}

// Pipeline age tiers — used to filter and visually grade cards so "New" actually means new.
const AGE_TIERS = [
  { key: "all",    label: "All ages",           test: () => true,                              accent: "" },
  { key: "fresh",  label: "Fresh (≤ 30 days)",  test: (d) => d != null && d <= 30,             accent: "border-l-4 border-l-emerald-400" },
  { key: "recent", label: "Recent (30–90 days)",test: (d) => d != null && d > 30 && d <= 90,   accent: "border-l-4 border-l-amber-400" },
  { key: "stale",  label: "Stale (90+ days)",   test: (d) => d != null && d > 90,              accent: "border-l-4 border-l-stone-300" },
];

function ageTier(days) {
  if (days == null) return null;
  if (days <= 30) return "fresh";
  if (days <= 90) return "recent";
  return "stale";
}

function AgeBadge({ days }) {
  if (days == null) return null;
  const tier = ageTier(days);
  const cls = tier === "fresh"  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : tier === "recent" ? "bg-amber-50 text-amber-800 border-amber-200"
            : "bg-stone-100 text-stone-500 border-stone-200";
  return (
    <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider border ${cls}`}>
      {daysLabel(days)}
    </span>
  );
}

function ManualBadge({ addedBy }) {
  if (!addedBy) return null;
  return (
    <span title={`Added manually by ${addedBy}`} className="inline-flex items-center gap-0.5 text-[#dddd16]" data-testid="manual-badge">
      <Sparkles className="w-3 h-3" fill="#dddd16" stroke="#A89A00" strokeWidth={1} />
    </span>
  );
}

function StageBadge({ status }) {
  // Normalise any retired stages (demo_booked / converted) so historic
  // contacts still render a familiar badge after the May 2026 simplification.
  const norm = LEGACY_STAGE_FALLBACK[status] || status;
  const s = STAGE_MAP[norm];
  if (!s) return <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-stone-100 text-stone-500 border border-stone-200 rounded-full">{norm || "—"}</span>;
  return <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-full ${s.color}`}>{s.label}</span>;
}
function AddContactModal({ open, onClose, onCreated, defaultTarget = "franchise" }) {
  const [form, setForm] = useState({
    target: defaultTarget,
    first_name: "", last_name: "", email: "", telephone: "",
    address_line_1: "", address_line_2: "",
    city: "", county: "", postcode: "", country: "United Kingdom",
    establishment_name: "",
    referral_source: "", notes: "",
    pipeline_status: "new",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, target: defaultTarget }));
      setErr("");
    }
  }, [open, defaultTarget]);

  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name && !form.last_name && !form.email && !form.establishment_name) {
      setErr("Please provide a first name, last name, email, or establishment.");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      const payload = { ...form };
      if (payload.target !== "pipeline") delete payload.pipeline_status;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const r = await api.post("/contacts", payload);
      onCreated && onCreated(r.data.contact, form.target);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save contact.");
    } finally {
      setSubmitting(false);
    }
  };

  const TARGETS = [
    { key: "franchise", label: "Franchise Contacts", icon: Users },
    { key: "licence",   label: "Licence Contacts",   icon: UserPlus },
    { key: "general",   label: "General Contacts",   icon: Users },
    { key: "pipeline",  label: "Sales Pipeline",     icon: Briefcase },
  ];
  const REFERRALS = ["Instagram", "Facebook", "X", "TikTok", "Google", "Friend", "Word of Mouth", "Other"];

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-stone-950/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" data-testid="add-contact-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-2xl bg-white border border-stone-200 rounded-2xl shadow-2xl my-10">
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">New contact</div>
            <h2 className="text-xl font-display font-black text-stone-950 mt-1">Add a Contact</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="add-contact-close" className="w-9 h-9 flex items-center justify-center hover:bg-stone-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-2">Where should this contact land?</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {TARGETS.map((t) => {
                const active = form.target === t.key;
                const Icon = t.icon;
                return (
                  <button key={t.key} type="button" onClick={() => setForm((f) => ({ ...f, target: t.key }))}
                    data-testid={`add-target-${t.key}`}
                    className={`px-3 py-3 border rounded-xl text-xs font-bold uppercase tracking-wider flex flex-col items-center gap-1.5 transition-colors ${
                      active ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                    }`}>
                    <Icon className="w-4 h-4" />
                    <span className="text-[10px] leading-tight text-center">{t.label}</span>
                  </button>
                );
              })}
            </div>
            {form.target === "pipeline" && (
              <div className="mt-3">
                <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5">Starting pipeline stage</label>
                <select value={form.pipeline_status} onChange={set("pipeline_status")} data-testid="add-pipeline-stage"
                  className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900">
                  {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="First name" value={form.first_name} onChange={set("first_name")} testid="add-first-name" />
            <Field label="Last name" value={form.last_name} onChange={set("last_name")} testid="add-last-name" />
            <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="add-email" />
            <Field label="Telephone" value={form.telephone} onChange={set("telephone")} testid="add-telephone" />
            <Field label="Establishment" value={form.establishment_name} onChange={set("establishment_name")} testid="add-establishment" wide />
          </div>

          {/* Address — full UK-shape block so manually-added contacts
              carry the same address detail as Airtable / Gravity Forms
              imports. The "Country" field defaults to United Kingdom but
              is editable for the occasional overseas enquiry. */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-2">Address</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="1st line of address" value={form.address_line_1} onChange={set("address_line_1")} testid="add-address-1" wide />
              <Field label="2nd line of address" value={form.address_line_2} onChange={set("address_line_2")} testid="add-address-2" wide />
              <Field label="Town / City" value={form.city} onChange={set("city")} testid="add-city" />
              <Field label="County / State" value={form.county} onChange={set("county")} testid="add-county" />
              <Field label="Postcode" value={form.postcode} onChange={set("postcode")} testid="add-postcode" />
              <Field label="Country" value={form.country} onChange={set("country")} testid="add-country" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5">Where did they hear about us?</label>
              <select value={form.referral_source} onChange={set("referral_source")} data-testid="add-referral"
                className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900">
                <option value="">— Not specified —</option>
                {REFERRALS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5">Notes</label>
              <textarea value={form.notes} onChange={set("notes")} data-testid="add-notes" rows={3}
                className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900" />
            </div>
          </div>

          {err && <div className="px-4 py-3 border border-red-200 bg-red-50 text-sm text-red-800 rounded-xl flex items-center gap-2"><AlertCircle className="w-4 h-4" />{err}</div>}
        </div>

        <div className="px-6 py-4 border-t border-stone-200 flex items-center justify-end gap-2 bg-stone-50 rounded-b-2xl">
          <button type="button" onClick={onClose} data-testid="add-contact-cancel"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 rounded-lg">
            Cancel
          </button>
          <button type="submit" disabled={submitting} data-testid="add-contact-submit"
            className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-[#dddd16] hover:bg-[#aaaa11] text-stone-950 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Save contact
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5">{label}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid}
        className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900" />
    </div>
  );
}

// CSV header alias map — common Gravity Forms / Excel column variations
const CSV_HEADER_ALIASES = {
  first_name:         ["first name", "firstname", "given name", "fname"],
  last_name:          ["last name", "surname", "surname name", "lastname", "family name", "lname"],
  email:              ["email", "email address", "e-mail"],
  telephone:          ["telephone", "telephone number", "phone", "phone number", "mobile", "tel"],
  postcode:           ["postcode", "post code", "zip", "zip code", "postal code"],
  city:               ["city", "city / town", "town", "city/town"],
  country:            ["country"],
  establishment_name: ["establishment", "establishment name", "business", "business name", "company", "1st line of address", "address"],
  referral_source:    ["referral", "referral source", "where did you hear about us", "where did you hear about creative mojo", "how did you hear about us"],
  message:            ["message", "your message", "comments", "notes"],
  date:               ["date", "entry date", "submission date", "submitted at", "created", "created at"],
};

function parseCsv(text) {
  // Tolerant CSV parser handling quoted fields with embedded commas + newlines + ""-escape
  const rows = [];
  let row = [], cell = "", inQuotes = false, i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i += 2; continue; }
      if (ch === '"') { inQuotes = false; i++; continue; }
      cell += ch; i++; continue;
    }
    if (ch === '"') { inQuotes = true; i++; continue; }
    if (ch === ",") { row.push(cell); cell = ""; i++; continue; }
    if (ch === "\r") { i++; continue; }
    if (ch === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; i++; continue; }
    cell += ch; i++;
  }
  if (cell.length > 0 || row.length > 0) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => c && c.trim()));
}

function mapHeaders(headers) {
  const out = {};
  headers.forEach((h, idx) => {
    const norm = (h || "").trim().toLowerCase();
    for (const field of Object.keys(CSV_HEADER_ALIASES)) {
      if (CSV_HEADER_ALIASES[field].includes(norm) || norm === field) {
        out[field] = idx;
        break;
      }
    }
  });
  return out;
}

function ImportCsvModal({ open, onClose, onImported, defaultTarget = "licence" }) {
  const [step, setStep] = useState(1);          // 1: upload, 2: preview, 3: done
  const [target, setTarget] = useState(defaultTarget);
  const [pipelineStage, setPipelineStage] = useState("new");
  const [dedupe, setDedupe] = useState(true);
  const [filename, setFilename] = useState("");
  const [headers, setHeaders] = useState([]);
  const [mapped, setMapped] = useState({});
  const [parsedRows, setParsedRows] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setStep(1); setTarget(defaultTarget); setErr(""); setResult(null); setParsedRows([]); setHeaders([]); setMapped({}); setFilename(""); } }, [open, defaultTarget]);

  if (!open) return null;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr("");
    setFilename(f.name);
    try {
      const text = await f.text();
      const rows = parseCsv(text);
      if (rows.length < 2) { setErr("CSV looks empty — need a header row and at least one data row."); return; }
      const hdrs = rows[0].map((h) => (h || "").trim());
      const dataRows = rows.slice(1);
      const colMap = mapHeaders(hdrs);
      const matchedFields = Object.keys(colMap).length;
      if (matchedFields === 0) { setErr(`No recognised columns. CSV headers I saw: ${hdrs.join(", ")}`); return; }
      // Project each row into a typed object
      const proj = dataRows.map((r) => {
        const obj = {};
        Object.entries(colMap).forEach(([field, idx]) => { obj[field] = (r[idx] || "").trim(); });
        return obj;
      }).filter((o) => o.first_name || o.last_name || o.email || o.establishment_name);
      setHeaders(hdrs);
      setMapped(colMap);
      setParsedRows(proj);
      setStep(2);
    } catch (ex) {
      setErr("Could not read the file. Please upload a UTF-8 CSV.");
    }
  };

  const TARGETS = [
    { key: "licence",   label: "Licence Contacts",   icon: UserPlus },
    { key: "franchise", label: "Franchise Contacts", icon: Users },
    { key: "general",   label: "General Contacts",   icon: Users },
    { key: "pipeline",  label: "Sales Pipeline",     icon: Briefcase },
  ];

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { target, dedupe_by_email: dedupe, rows: parsedRows };
      if (target === "pipeline") payload.pipeline_status = pipelineStage;
      const r = await api.post("/contacts/import", payload);
      setResult(r.data);
      setStep(3);
      onImported && onImported(r.data, target);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-stone-950/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" data-testid="import-csv-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white border border-stone-200 rounded-2xl shadow-2xl my-10">
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Bulk import · Step {step} of 3</div>
            <h2 className="text-xl font-display font-black text-stone-950 mt-1">Import contacts from CSV</h2>
          </div>
          <button onClick={onClose} data-testid="import-close" className="w-9 h-9 flex items-center justify-center hover:bg-stone-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        {step === 1 && (
          <div className="p-6 space-y-5">
            <div className="text-sm text-stone-700">
              Upload a CSV exported from Gravity Forms, Mailchimp, or any spreadsheet. We auto-detect the following columns (case-insensitive, common aliases supported):
            </div>
            <div className="grid grid-cols-2 gap-1 text-xs text-stone-600">
              {Object.keys(CSV_HEADER_ALIASES).map((f) => <div key={f}>· <code className="font-mono">{f}</code></div>)}
            </div>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-2">CSV file</div>
              <div className="border-2 border-dashed border-stone-300 rounded-xl p-6 text-center hover:border-stone-500 transition-colors cursor-pointer">
                <Upload className="w-6 h-6 mx-auto text-stone-400 mb-2" />
                <div className="text-sm text-stone-700">Click to choose, or drop a CSV file here</div>
                <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="csv-file-input" />
              </div>
            </label>
            {err && <div className="px-4 py-3 border border-red-200 bg-red-50 text-sm text-red-800 rounded-xl flex items-center gap-2"><AlertCircle className="w-4 h-4 shrink-0" /><span>{err}</span></div>}
          </div>
        )}

        {step === 2 && (
          <div className="p-6 space-y-5">
            <div className="px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-stone-500" />
              <span><strong>{filename}</strong> — {parsedRows.length} usable rows · {Object.keys(mapped).length} columns mapped</span>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-2">Send these contacts to</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {TARGETS.map((t) => {
                  const active = target === t.key;
                  const Icon = t.icon;
                  return (
                    <button key={t.key} onClick={() => setTarget(t.key)} data-testid={`import-target-${t.key}`}
                      className={`px-3 py-3 border rounded-xl text-xs font-bold uppercase tracking-wider flex flex-col items-center gap-1.5 ${
                        active ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                      }`}>
                      <Icon className="w-4 h-4" /> <span className="text-[10px] leading-tight text-center">{t.label}</span>
                    </button>
                  );
                })}
              </div>
              {target === "pipeline" && (
                <div className="mt-3">
                  <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5">Starting pipeline stage</label>
                  <select value={pipelineStage} onChange={(e) => setPipelineStage(e.target.value)} data-testid="import-pipeline-stage"
                    className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg">
                    {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={dedupe} onChange={(e) => setDedupe(e.target.checked)} data-testid="import-dedupe" />
              <span>Skip rows whose email already exists in the CRM (recommended)</span>
            </label>

            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-2">Preview · first 5 rows</div>
              <div className="border border-stone-200 rounded-xl overflow-x-auto max-h-64">
                <table className="w-full text-xs">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>{Object.keys(mapped).map((f) => <th key={f} className="px-2 py-1.5 text-left font-bold text-stone-700">{f}</th>)}</tr>
                  </thead>
                  <tbody>
                    {parsedRows.slice(0, 5).map((r, i) => (
                      <tr key={`row-${i}-${Object.values(r).slice(0, 3).join("|")}`} className="border-b border-stone-100 last:border-0">
                        {Object.keys(mapped).map((f) => <td key={f} className="px-2 py-1 text-stone-700 whitespace-nowrap">{r[f] || "—"}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {err && <div className="px-4 py-3 border border-red-200 bg-red-50 text-sm text-red-800 rounded-xl flex items-center gap-2"><AlertCircle className="w-4 h-4 shrink-0" /><span>{err}</span></div>}
          </div>
        )}

        {step === 3 && result && (
          <div className="p-6 space-y-3">
            <div className="px-4 py-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-bold text-emerald-900">Imported {result.inserted} contact{result.inserted === 1 ? "" : "s"}</div>
                <div className="text-xs text-emerald-800 mt-1">
                  Sent to <strong>{TARGETS.find((t) => t.key === result.target)?.label || result.target}</strong>.
                  {result.skipped_empty ? ` Skipped ${result.skipped_empty} empty rows.` : ""}
                  {result.skipped_duplicate ? ` Skipped ${result.skipped_duplicate} duplicate emails.` : ""}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t border-stone-200 flex items-center justify-end gap-2 bg-stone-50 rounded-b-2xl">
          {step === 2 && (
            <button onClick={() => setStep(1)} className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 rounded-lg">
              ← Back
            </button>
          )}
          <button onClick={onClose} data-testid="import-cancel"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 rounded-lg">
            {step === 3 ? "Close" : "Cancel"}
          </button>
          {step === 2 && (
            <button onClick={submit} disabled={busy || parsedRows.length === 0} data-testid="import-submit"
              className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-[#dddd16] hover:bg-[#aaaa11] text-stone-950 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Import {parsedRows.length} contact{parsedRows.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function sourceLabel(s) {
  return (SOURCE_STYLE[s]?.label) || s || "Other";
}

function SourcePill({ source }) {
  const sty = SOURCE_STYLE[source] || SOURCE_STYLE.general_enquiry;
  return (
    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-md ${sty.pill}`}>
      {sty.label}
    </span>
  );
}

function ReferralBadge({ source }) {
  if (!source) return null;
  const Icon = REFERRAL_ICONS[source] || HelpCircle;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold bg-stone-100 text-stone-800 border border-stone-200 rounded-md">
      <Icon className="w-3 h-3" /> {source}
    </span>
  );
}

// Compact "Move to…" dropdown used both on rows (compact) and the bulk action bar
function MoveMenu({ onMove, onDelete, label = "Move", testid, currentTab, count, contactSource, inPipeline }) {
  const [open, setOpen] = useState(false);
  const [showStages, setShowStages] = useState(false);
  const close = () => { setOpen(false); setShowStages(false); };
  // Highlight the contact's CURRENT source so the user sees what they're changing FROM.
  const currentType =
    contactSource === "licence_enquiry" ? "licence"
    : contactSource === "franchise_enquiry" ? "franchise"
    : contactSource === "general_enquiry" ? "general"
    : null;
  return (
    <div className="relative inline-block" onMouseLeave={close} data-testid={testid}>
      <button onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); setShowStages(false); }}
        className="px-2.5 py-1 text-xs font-bold uppercase tracking-wider bg-white border border-stone-300 text-stone-800 hover:bg-stone-50 rounded-lg flex items-center gap-1"
        data-testid={`${testid}-trigger`}>
        <ArrowRightLeft className="w-3 h-3" /> {label}{count != null && count > 0 ? ` (${count})` : ""}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden text-sm text-stone-900">
          {!showStages ? (
            <>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 bg-stone-50 border-b border-stone-200">Change Type</div>
              <button onClick={(e) => { e.stopPropagation(); onMove("franchise"); close(); }} data-testid={`${testid}-franchise`}
                disabled={currentType === "franchise"}
                className="w-full text-left px-3 py-2 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                <Users className="w-3.5 h-3.5 text-stone-500" /> Franchise{currentType === "franchise" && <span className="ml-auto text-[9px] text-stone-400 uppercase tracking-wider">current</span>}
              </button>
              <button onClick={(e) => { e.stopPropagation(); onMove("licence"); close(); }} data-testid={`${testid}-licence`}
                disabled={currentType === "licence"}
                className="w-full text-left px-3 py-2 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                <UserPlus className="w-3.5 h-3.5 text-indigo-500" /> Licence{currentType === "licence" && <span className="ml-auto text-[9px] text-stone-400 uppercase tracking-wider">current</span>}
              </button>
              <button onClick={(e) => { e.stopPropagation(); onMove("general"); close(); }} data-testid={`${testid}-general`}
                disabled={currentType === "general"}
                className="w-full text-left px-3 py-2 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                <Users className="w-3.5 h-3.5 text-stone-500" /> General{currentType === "general" && <span className="ml-auto text-[9px] text-stone-400 uppercase tracking-wider">current</span>}
              </button>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 bg-stone-50 border-y border-stone-200">Pipeline</div>
              <button onClick={(e) => { e.stopPropagation(); setShowStages(true); }} data-testid={`${testid}-pipeline`}
                className="w-full text-left px-3 py-2 hover:bg-stone-50 flex items-center justify-between">
                <span className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-stone-500" /> {inPipeline ? "Change Pipeline Stage" : "Add to Pipeline"}</span>
                <ChevronDown className="w-3 h-3 -rotate-90 text-stone-400" />
              </button>
              {inPipeline && (
                <button onClick={(e) => { e.stopPropagation(); onMove("remove_from_pipeline"); close(); }} data-testid={`${testid}-remove-from-pipeline`}
                  className="w-full text-left px-3 py-2 hover:bg-stone-50 flex items-center gap-2 text-red-700">
                  <X className="w-3.5 h-3.5" /> Remove from Pipeline
                </button>
              )}
              {onDelete && (
                <>
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 bg-stone-50 border-y border-stone-200">Danger zone</div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(); close(); }}
                    data-testid={`${testid}-bulk-delete`}
                    className="w-full text-left px-3 py-2 hover:bg-red-50 flex items-center gap-2 text-red-700 font-bold"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete selected{count != null && count > 0 ? ` (${count})` : ""}
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 bg-stone-50 border-b border-stone-200">
                {inPipeline ? "Change stage to" : "Add to pipeline as"}
              </div>
              {STAGES.filter((s) => contactSource !== "licence_enquiry" || !FRANCHISE_ONLY_STAGES.has(s.key)).map((s) => (
                <button key={s.key} onClick={(e) => { e.stopPropagation(); onMove("pipeline", s.key); close(); }}
                  data-testid={`${testid}-stage-${s.key}`}
                  className="w-full text-left px-3 py-2 hover:bg-stone-50 flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${s.barColor}`} />
                  {s.label}
                </button>
              ))}
              <button onClick={(e) => { e.stopPropagation(); setShowStages(false); }} className="w-full text-left px-3 py-2 hover:bg-stone-50 text-stone-500 border-t border-stone-100 text-xs">
                ← Back
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ContactDrawer({ contact, onClose, onStageChange, onPromote, onDemote, onDelete, onReply, onConvert, onLinkExisting, onAdminNotesUpdated, onMergeWith, onChecklistChanged, onChangeSource, onMarkFollowUpSent, allContacts }) {
  const [busy, setBusy] = useState(false);
  const [converting, setConverting] = useState(false);
  const [mcBusy, setMcBusy] = useState(false);  // "Remove from Mailchimp" in-flight guard
  const [replyModalOpen, setReplyModalOpen] = useState(false);  // Reply-with-template modal
  // UK-wide territory-atlas modal, opened by the compact "Map" button
  // next to the postcode line. Renders a red pin at the contact's
  // postcode over a full colour-coded map of every franchisee's
  // territory so HQ can see at a glance where the enquiry sits.
  const [postcodeMapContact, setPostcodeMapContact] = useState(null);
  // Bumped after a successful send so <EmailTimeline> re-fetches and
  // surfaces the new entry without a page reload.
  const [emailRefreshSignal, setEmailRefreshSignal] = useState(0);
  // Inline edit of identity + address — staff routinely fix typos from
  // Gravity-Forms / Airtable imports. We hold a single ``editing``
  // boolean + a working ``draft`` copy that's only persisted on Save.
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState({});
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsErr, setDetailsErr] = useState("");
  // Inline "Change type" picker — lets the admin reclassify an enquiry
  // between Franchise / Licence / General without leaving the drawer
  // (covers the common case where the contact filled in the wrong web form).
  const [sourceMenuOpen, setSourceMenuOpen] = useState(false);
  // Reset edit mode whenever the drawer switches contacts so half-edits
  // don't leak from one contact to another. Sits BEFORE the early-return
  // below to comply with React's Hook rules.
  useEffect(() => {
    setEditingDetails(false);
    setDetailsErr("");
  }, [contact?.id]);
  // Fetch any territory plan already linked to this contact so the
  // "Plan their territory" card can flip its CTA from "Open builder"
  // to a shortcut opening the linked plan directly. Ran per-contact
  // so switching between rows always shows the correct state.
  const [linkedPlan, setLinkedPlan] = useState(null);
  useEffect(() => {
    if (!contact?.id) { setLinkedPlan(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/territory-plans", { params: { contact_id: contact.id } });
        const plan = (data?.plans || [])[0] || null;
        if (alive) setLinkedPlan(plan);
      } catch {
        if (alive) setLinkedPlan(null);
      }
    })();
    return () => { alive = false; };
  }, [contact?.id]);
  if (!contact) return null;
  const beginEditDetails = () => {
    setDetailsDraft({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      email: contact.email || contact.email_raw || "",
      telephone: contact.telephone || "",
      mobile_phone: contact.mobile_phone || "",
      address_line_1: contact.address_line_1 || contact.address_street || "",
      address_line_2: contact.address_line_2 || "",
      city: contact.city || contact.town_city || "",
      county: contact.county || "",
      postcode: contact.postcode || "",
      country: contact.country || "United Kingdom",
    });
    setDetailsErr("");
    setEditingDetails(true);
  };
  const saveDetails = async () => {
    setDetailsSaving(true); setDetailsErr("");
    try {
      const { data } = await api.patch(`/contacts/${contact.id}/details`, detailsDraft);
      // Forward the saved fields up — the parent's onChecklistChanged
      // patch already filters undefineds, so we reuse it as a generic
      // "fields-merge" channel without adding another prop.
      onChecklistChanged?.({
        id: contact.id,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        telephone: data.telephone,
        mobile_phone: data.mobile_phone,
        address_line_1: data.address_line_1,
        address_line_2: data.address_line_2,
        address_street: data.address_street,
        city: data.city,
        town_city: data.town_city,
        county: data.county,
        postcode: data.postcode,
        country: data.country,
      });
      setEditingDetails(false);
    } catch (e) {
      setDetailsErr(e?.response?.data?.detail || "Could not save changes.");
    } finally { setDetailsSaving(false); }
  };
  const setDraft = (k) => (e) => setDetailsDraft((d) => ({ ...d, [k]: e.target.value }));
  const isInPipeline = !!contact.in_pipeline;
  const dateAdded = contact.date || contact.date_added;
  const sinceCreated = daysSince(dateAdded);
  const isFranchiseEnq = contact.source === "franchise_enquiry";
  const isLicenceEnq = contact.source === "licence_enquiry";
  const homeTabLabel = isLicenceEnq ? "Licence Contacts" : isFranchiseEnq ? "Franchise Contacts" : "General Contacts";
  const convertLabel = isLicenceEnq ? "Convert to Licencee" : "Convert to Franchisee";
  const alreadyConverted = !!contact.converted_to_franchisee_id;

  const confirmDelete = async () => {
    if (!window.confirm("Permanently delete this contact? This cannot be undone.")) return;
    setBusy(true); try { await onDelete(contact.id); } finally { setBusy(false); }
  };

  // One-shot "Remove from Mailchimp" — manual button, independent of
  // Lost status. Does NOT touch the HUB contact; only PATCHes the
  // Mailchimp audience member to status=unsubscribed. Toasts the exact
  // outcome (unsubscribed / already-unsubscribed / not-found / not-
  // configured) so the admin knows which case fired. ``mcBusy`` is
  // declared with the other drawer hooks above the early return.
  const removeFromMailchimp = async () => {
    if (mcBusy) return;
    setMcBusy(true);
    try {
      const { data } = await api.post(`/contacts/${contact.id}/mailchimp/unsubscribe`);
      if (data?.status === "unsubscribed") {
        toast.success(data.message || "Unsubscribed from Mailchimp.");
      } else if (data?.status === "already_unsubscribed") {
        toast(data.message || "Already unsubscribed in Mailchimp.");
      } else {
        toast(data?.message || "Mailchimp request completed.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : (detail?.message || err?.message || "Mailchimp request failed.");
      const status = detail?.status;
      if (status === "not_found") toast.error(message);
      else if (status === "not_configured") toast.error(message);
      else toast.error(message);
    } finally {
      setMcBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="contact-drawer">
      <div className="flex-1 bg-stone-900/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="w-full max-w-xl bg-white border-l border-stone-200 overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">
            {isInPipeline ? "Sales Pipeline" : homeTabLabel}
          </div>
          <div className="flex items-center gap-1">
            {contact.email && (
              <button onClick={() => setReplyModalOpen(true)} data-testid="drawer-reply-template"
                title="Reply using a saved Resend template"
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-[#dddd16] hover:bg-[#aaaa11] text-stone-950 rounded-lg flex items-center gap-1">
                <Mail className="w-3.5 h-3.5" /> Reply with template
              </button>
            )}
            {contact.email && onReply && (
              <button onClick={() => onReply(contact)} data-testid="drawer-reply"
                title="Open a quick blank reply in your own mail app"
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 rounded-lg flex items-center gap-1">
                <Send className="w-3.5 h-3.5" /> Quick reply
              </button>
            )}
            {isInPipeline && (!contact.pipeline_status || contact.pipeline_status === "new") && (
              <button onClick={() => onStageChange(contact.id, "contacted")}
                data-testid="drawer-mark-contacted"
                title="Mark Contacted — use this if you've replied in your own email app"
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-white border border-stone-300 text-stone-700 hover:bg-stone-950 hover:text-white hover:border-stone-950 rounded-lg flex items-center gap-1 transition-colors">
                <CheckCircle2 className="w-3.5 h-3.5" /> Mark contacted
              </button>
            )}
            <button onClick={confirmDelete} disabled={busy} data-testid="drawer-delete"
              className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-red-700 hover:bg-red-50 rounded-lg flex items-center gap-1 disabled:opacity-50">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
            {(contact.email || contact.email_raw) && (
              <button
                onClick={removeFromMailchimp}
                disabled={mcBusy}
                data-testid="drawer-mailchimp-unsubscribe"
                title="Unsubscribe this contact's email from the configured Mailchimp audience"
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 rounded-lg flex items-center gap-1 disabled:opacity-50"
              >
                {mcBusy
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <MailX className="w-3.5 h-3.5" />}
                Remove from Mailchimp
              </button>
            )}
            <button onClick={onClose} data-testid="drawer-close" className="p-2 text-stone-500 hover:text-stone-950 rounded-lg hover:bg-stone-100">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h2 className="font-display text-3xl text-stone-950 flex items-center gap-2">
              {editingDetails ? (
                <span className="flex gap-2 w-full">
                  <input
                    value={detailsDraft.first_name || ""}
                    onChange={setDraft("first_name")}
                    placeholder="First name"
                    data-testid="drawer-edit-first-name"
                    className="flex-1 min-w-0 px-3 py-1.5 text-base font-sans font-semibold bg-white border border-stone-300 rounded-lg focus:outline-none focus:border-stone-900"
                  />
                  <input
                    value={detailsDraft.last_name || ""}
                    onChange={setDraft("last_name")}
                    placeholder="Last name"
                    data-testid="drawer-edit-last-name"
                    className="flex-1 min-w-0 px-3 py-1.5 text-base font-sans font-semibold bg-white border border-stone-300 rounded-lg focus:outline-none focus:border-stone-900"
                  />
                </span>
              ) : (
                <>
                  <span>{[contact.first_name, contact.last_name].filter(Boolean).join(" ") || "(no name)"}</span>
                  <ManualBadge addedBy={contact.manually_added_by} />
                </>
              )}
            </h2>
            {contact.establishment_name && <div className="text-base text-stone-600 mt-1">{contact.establishment_name}</div>}
            <div className="flex items-center gap-2 flex-wrap mt-3">
              {isInPipeline && <StageBadge status={contact.pipeline_status} />}
              {isInPipeline && <TemperatureFlame value={contact.temperature} />}
              {/* Phase 4 — automatic lead-temperature score (Hot/Warm/Cold)
                  derived from email opens/clicks + landing-page activity.
                  Sits alongside Sandra's manual flame as a data signal. */}
              <LeadTemperatureBadge contactId={contact.id} refreshSignal={emailRefreshSignal} />
              <SourcePill source={contact.source} />
              {onChangeSource && (contact.source === "franchise_enquiry" || contact.source === "licence_enquiry" || contact.source === "general_enquiry") && (
                <div className="relative" data-testid="drawer-change-source">
                  <button
                    type="button"
                    onClick={() => setSourceMenuOpen((v) => !v)}
                    data-testid="drawer-change-source-trigger"
                    title="Reclassify this enquiry"
                    className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 rounded-md inline-flex items-center gap-1">
                    <ArrowRightLeft className="w-3 h-3" /> Change type
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {sourceMenuOpen && (
                    <div onMouseLeave={() => setSourceMenuOpen(false)}
                      className="absolute left-0 top-full mt-1 z-50 w-56 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden text-sm text-stone-900">
                      <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 bg-stone-50 border-b border-stone-200">Reclassify enquiry</div>
                      {[
                        { key: "franchise", label: "Franchise enquiry", source: "franchise_enquiry", Icon: Users, iconClass: "text-stone-500" },
                        { key: "licence",   label: "Licence enquiry",   source: "licence_enquiry",   Icon: UserPlus, iconClass: "text-indigo-500" },
                        { key: "general",   label: "General enquiry",   source: "general_enquiry",   Icon: Users, iconClass: "text-stone-500" },
                      ].map(({ key, label, source, Icon, iconClass }) => {
                        const current = contact.source === source;
                        return (
                          <button key={key} type="button"
                            disabled={current}
                            onClick={async () => {
                              setSourceMenuOpen(false);
                              await onChangeSource(contact, key);
                            }}
                            data-testid={`drawer-change-source-${key}`}
                            className="w-full text-left px-3 py-2 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                            <Icon className={`w-3.5 h-3.5 ${iconClass}`} />
                            <span className="flex-1">{label}</span>
                            {current && <span className="text-[9px] text-stone-400 uppercase tracking-wider">current</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {contact.referral_source && <ReferralBadge source={contact.referral_source} />}
            </div>
            {contact.referral_source && (
              <div className="text-xs text-stone-500 mt-2">
                Heard about Creative Mojo via <strong>{contact.referral_source}</strong>
              </div>
            )}
            {contact.manually_added_by && (
              <div className="mt-3 px-3 py-2 bg-[#dddd16]/10 border border-[#dddd16]/40 rounded-lg flex items-center gap-2 text-xs text-stone-800" data-testid="drawer-manual-flag">
                <Sparkles className="w-3.5 h-3.5 text-stone-700" fill="#dddd16" />
                <span>
                  Added manually by <strong>{contact.manually_added_by}</strong>
                  {contact.created_at && (
                    <> on <strong>{(() => {
                      const s = String(contact.created_at);
                      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
                      return m ? `${m[3]}/${m[2]}/${m[1]}` : s.slice(0, 10);
                    })()}</strong></>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* 3. Contact info / address — view + inline edit. The pencil
              icon in the corner toggles edit mode for the whole panel
              and the name above. Saved fields are mirrored into the
              parent's cached list via onChecklistChanged. */}
          <div className="bg-stone-50 border border-stone-200 p-4 space-y-3 text-sm rounded-xl relative" data-testid="drawer-contact-info">
            {!editingDetails && (
              <button
                type="button"
                onClick={beginEditDetails}
                data-testid="drawer-edit-details"
                title="Edit contact details"
                className="absolute top-2 right-2 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-500 hover:text-stone-900 hover:bg-stone-200 rounded-md inline-flex items-center gap-1">
                <Pencil className="w-3 h-3" /> Edit
              </button>
            )}
            {editingDetails ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">Email</label>
                    <input type="email" value={detailsDraft.email || ""} onChange={setDraft("email")} data-testid="drawer-edit-email"
                      className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-md text-sm focus:outline-none focus:border-stone-900" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">Telephone</label>
                    <input value={detailsDraft.telephone || ""} onChange={setDraft("telephone")} data-testid="drawer-edit-telephone"
                      className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-md text-sm focus:outline-none focus:border-stone-900" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">1st line of address</label>
                    <input value={detailsDraft.address_line_1 || ""} onChange={setDraft("address_line_1")} data-testid="drawer-edit-address-1"
                      className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-md text-sm focus:outline-none focus:border-stone-900" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">2nd line of address</label>
                    <input value={detailsDraft.address_line_2 || ""} onChange={setDraft("address_line_2")} data-testid="drawer-edit-address-2"
                      className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-md text-sm focus:outline-none focus:border-stone-900" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">Town / City</label>
                    <input value={detailsDraft.city || ""} onChange={setDraft("city")} data-testid="drawer-edit-city"
                      className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-md text-sm focus:outline-none focus:border-stone-900" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">County / State</label>
                    <input value={detailsDraft.county || ""} onChange={setDraft("county")} data-testid="drawer-edit-county"
                      className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-md text-sm focus:outline-none focus:border-stone-900" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">Postcode</label>
                    <input value={detailsDraft.postcode || ""} onChange={setDraft("postcode")} data-testid="drawer-edit-postcode"
                      className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-md text-sm focus:outline-none focus:border-stone-900" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">Country</label>
                    <input value={detailsDraft.country || ""} onChange={setDraft("country")} data-testid="drawer-edit-country"
                      className="w-full px-3 py-1.5 bg-white border border-stone-300 rounded-md text-sm focus:outline-none focus:border-stone-900" />
                  </div>
                </div>
                {detailsErr && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{detailsErr}</div>}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setEditingDetails(false)} disabled={detailsSaving} data-testid="drawer-edit-cancel"
                    className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-stone-300 bg-white hover:bg-stone-50 rounded-md disabled:opacity-50">
                    Cancel
                  </button>
                  <button type="button" onClick={saveDetails} disabled={detailsSaving} data-testid="drawer-edit-save"
                    className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-[#dddd16] hover:bg-[#aaaa11] text-stone-950 rounded-md disabled:opacity-50 inline-flex items-center gap-1">
                    {detailsSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <>
                {(contact.email || contact.email_raw) && (
                  <div className="flex items-start gap-2"><Mail className="w-3.5 h-3.5 text-stone-400 mt-1" />
                    <a href={`mailto:${contact.email || contact.email_raw}`} className="text-stone-900 hover:underline">{contact.email || contact.email_raw}</a></div>
                )}
                {(contact.telephone || contact.mobile_phone) && (
                  <div className="flex items-start gap-2"><Phone className="w-3.5 h-3.5 text-stone-400 mt-1" />
                    <span className="text-stone-900">{contact.telephone || contact.mobile_phone}</span></div>
                )}
                {(() => {
                  // Address rendered as a multi-line block — pulls from the
                  // various legacy field aliases so Airtable / Gravity Forms
                  // / manual entries all render consistently.
                  const line1 = contact.address_line_1 || contact.address_street;
                  const line2 = contact.address_line_2;
                  const town  = contact.city || contact.town_city;
                  const lines = [line1, line2, town, contact.county, contact.postcode, contact.country].filter(Boolean);
                  if (lines.length === 0) return null;
                  return (
                    <div className="flex items-start gap-2" data-testid="drawer-address">
                      <MapPin className="w-3.5 h-3.5 text-stone-400 mt-1 shrink-0" />
                      <div className="text-stone-900 leading-snug">
                        {lines.map((ln, i) => {
                          const isPostcode = contact.postcode && ln === contact.postcode;
                          return (
                            <div key={`addr-${i}-${ln}`} className={isPostcode ? "flex items-center gap-2" : ""}>
                              <span>{ln}</span>
                              {isPostcode && (
                                <button
                                  type="button"
                                  onClick={() => setPostcodeMapContact(contact)}
                                  data-testid="drawer-postcode-map-btn"
                                  title={`See ${contact.postcode} on the UK territory atlas`}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-stone-300 rounded hover:bg-stone-100 text-stone-700"
                                >
                                  <MapPin className="w-2.5 h-2.5" /> Map
                                </button>
                              )}
                            </div>
                          );
                        })}
                  </div>
                </div>
              );
            })()}
                {dateAdded && (
                  <div className="flex items-start gap-2"><Calendar className="w-3.5 h-3.5 text-stone-400 mt-1" />
                    <span className="text-stone-900">{formatDate(dateAdded)} <span className="text-stone-500">· {sinceCreated} days ago</span></span></div>
                )}
              </>
            )}
          </div>

          {/* 4–6. Interested-only block: Checklist → Plan Territory → Convert.
              These three panels are intentionally hidden in New / Contacted /
              Dormant / Lost because they make no sense until someone is
              actively being qualified. */}
          {isInPipeline && contact.pipeline_status === "qualified" && !alreadyConverted && (
            <InterestedChecklist contact={contact} onChanged={onChecklistChanged} />
          )}

          {isInPipeline && contact.pipeline_status === "qualified" && !alreadyConverted && !isLicenceEnq && (
            <a href={linkedPlan ? `/territory-builder?plan_id=${linkedPlan.id}` : `/territory-builder?contact_id=${contact.id}`}
              data-testid="drawer-plan-territory"
              className={`block p-4 border rounded-xl transition-all ${
                linkedPlan
                  ? "border-emerald-300 bg-gradient-to-br from-emerald-50 to-white hover:border-emerald-500 hover:shadow-md"
                  : "border-stone-300 bg-gradient-to-br from-[#EEEE86]/30 to-white hover:border-stone-500 hover:shadow-md"
              }`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-stone-950 flex items-center gap-1.5">
                    <Target className={`w-4 h-4 ${linkedPlan ? "text-emerald-700" : "text-stone-700"}`} />
                    {linkedPlan ? "Territory plan linked" : "Plan their territory"}
                  </div>
                  <div className="text-xs text-stone-600 mt-1">
                    {linkedPlan ? (
                      <>
                        Linked plan: <strong>{linkedPlan.name || "Untitled"}</strong>
                        {typeof linkedPlan.total_homes === "number" && (
                          <span className="text-stone-500"> · {linkedPlan.total_homes} homes</span>
                        )}
                        {typeof linkedPlan.sectors?.length === "number" && (
                          <span className="text-stone-500"> · {linkedPlan.sectors.length} sectors</span>
                        )}
                        {linkedPlan.is_shared && (
                          <span className="ml-1.5 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-emerald-600 text-white rounded">Shared</span>
                        )}
                        <span className="block mt-1 text-stone-500">Open the plan to edit sectors, tweak the coverage, or copy the share link.</span>
                      </>
                    ) : (
                      <>
                        Build a sample 150-home territory around their postcode using live CQC data. Saved against this contact and easy to edit.
                        <span className="block mt-1 text-stone-500">Already drafted one for them elsewhere? Open the builder and click <strong>Link contact</strong> on the matching saved plan.</span>
                      </>
                    )}
                  </div>
                </div>
                <span className={`shrink-0 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-lg flex items-center gap-1.5 ${
                  linkedPlan ? "bg-emerald-700 text-white" : "bg-stone-950 text-white"
                }`}>
                  {linkedPlan ? "See linked plan" : "Open builder"}
                </span>
              </div>
            </a>
          )}

          {isInPipeline && contact.pipeline_status === "qualified" && (
            <div className={`p-4 border rounded-xl ${alreadyConverted ? "bg-emerald-50 border-emerald-200" : "bg-gradient-to-br from-[#dddd16]/10 to-stone-50 border-stone-300"}`} data-testid="drawer-convert-section">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-stone-950 flex items-center gap-1.5">
                    <Award className={`w-4 h-4 ${alreadyConverted ? "text-emerald-700" : "text-stone-700"}`} />
                    {alreadyConverted ? "Already converted" : convertLabel}
                  </div>
                  <div className="text-xs text-stone-600 mt-1">
                    {alreadyConverted
                      ? <>This contact has been converted to a {contact.converted_to_record_type === "licencee" ? "Licencee" : "Franchisee"} record.</>
                      : <>Create a {isLicenceEnq ? "Licencee" : "Franchisee"} record from this enquiry. Their details &amp; original message will copy over.</>}
                  </div>
                </div>
                {alreadyConverted ? (
                  <button
                    onClick={() => onConvert(contact, true)}
                    data-testid="drawer-view-franchisee"
                    className="shrink-0 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-white text-emerald-800 border border-emerald-300 hover:bg-emerald-100 rounded-lg flex items-center gap-1.5">
                    <ArrowRightLeft className="w-3.5 h-3.5" /> View record
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`${convertLabel} for ${[contact.first_name, contact.last_name].filter(Boolean).join(" ") || "this contact"}?`)) return;
                      setConverting(true);
                      try { await onConvert(contact, false); }
                      finally { setConverting(false); }
                    }}
                    disabled={converting}
                    data-testid="drawer-convert"
                    className="shrink-0 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                    <Award className="w-3.5 h-3.5" /> {converting ? "Converting…" : convertLabel}
                  </button>
                )}
              </div>
              {!alreadyConverted && onLinkExisting && (
                <div className="mt-3 pt-3 border-t border-stone-200/70 flex items-center justify-between gap-3">
                  <div className="text-xs text-stone-600">
                    Already in the franchisees list? Skip creating a new record and link to the existing one.
                  </div>
                  <button
                    onClick={() => onLinkExisting(contact)}
                    data-testid="drawer-link-existing"
                    className="shrink-0 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-white text-stone-800 border border-stone-300 hover:bg-stone-50 rounded-lg flex items-center gap-1.5">
                    <Link2 className="w-3.5 h-3.5" /> Link to existing
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 7. Move to stage (pipeline) OR Promote (non-pipeline) */}
          {isInPipeline ? (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Move to stage</div>
              <div className="grid grid-cols-3 gap-2" data-testid="drawer-stages">
                {stagesForContact(contact).map((s) => (
                  <button key={s.key} onClick={() => onStageChange(contact.id, s.key)} data-testid={`drawer-stage-${s.key}`}
                    className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border rounded-lg transition-colors ${
                      contact.pipeline_status === s.key ? `${s.color} border-current` : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                    }`}>{s.label}</button>
                ))}
              </div>
              <button onClick={() => onDemote(contact.id)} data-testid="drawer-demote"
                className="mt-3 px-4 py-2 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 rounded-lg flex items-center gap-1.5">
                <ArrowDownCircle className="w-3.5 h-3.5" /> Remove from sales pipeline
              </button>
              {/* Retroactive "follow-up already sent" flag — for contacts
                  HQ chased BEFORE the 7-day automation existed. Stamps
                  ``follow_up_sent_count = 1`` and ``auto_followed_up_at``
                  so the scheduler skips them permanently. */}
              {onMarkFollowUpSent && Number(contact.follow_up_sent_count || 0) === 0 && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="text-xs text-amber-900 mb-2">
                    Already sent a follow-up outside the system? Mark it as done so this contact
                    won&apos;t drop into <strong>Follow-up Due</strong>.
                  </div>
                  <button
                    onClick={() => onMarkFollowUpSent(contact.id)}
                    data-testid="drawer-mark-follow-up-sent"
                    className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-amber-600 text-white hover:bg-amber-700 rounded-lg flex items-center gap-1.5"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark follow-up already sent
                  </button>
                </div>
              )}
              {onMarkFollowUpSent && Number(contact.follow_up_sent_count || 0) >= 1 && (
                <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md" data-testid="drawer-follow-up-already-sent">
                  <CheckCircle2 className="w-3 h-3" /> Follow-up recorded
                </div>
              )}
              <div className="text-xs text-stone-500 mt-1.5">
                Will return to <strong>{homeTabLabel}</strong> based on their source.
              </div>
            </div>
          ) : (
            <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl">
              <div className="text-sm font-semibold text-stone-900 mb-1">Not in the sales pipeline</div>
              <div className="text-xs text-stone-600 mb-3">
                Currently in <strong>{homeTabLabel}</strong>.
                Promote them to actively work them as a potential franchisee.
              </div>
              <button onClick={() => onPromote(contact.id)} data-testid="drawer-promote"
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 hover:bg-[#aaaa11] rounded-lg flex items-center gap-1.5">
                <ArrowUpCircle className="w-3.5 h-3.5" /> Promote to sales pipeline
              </button>
            </div>
          )}

          {/* 8. Sales Notes — only in pipeline */}
          {isInPipeline && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Sales Notes</div>
              <div className="bg-white border border-stone-200 divide-y divide-stone-100 text-sm rounded-xl overflow-hidden">
                {[
                  ["Response Sent", contact.response_sent], ["Email Opened", contact.email_opened],
                  ["Potential", contact.potential], ["Price Tier", contact.price_tier],
                  ["Had a Map", contact.had_a_map], ["Shadow Booked", contact.shadow_booked],
                  ["Follow Up Needed", contact.follow_up_needed], ["Country", contact.country_tag],
                ].filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                  <div key={k} className="px-3 py-2 flex justify-between gap-3">
                    <span className="text-xs uppercase tracking-wider text-stone-500 font-bold">{k}</span>
                    <span className="text-stone-900 text-right">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 9. Notes (Original Enquiry) */}
          {(contact.why_contacting || contact.message) && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Notes</div>
              <div className="bg-stone-50 border border-stone-200 p-4 text-sm text-stone-800 leading-relaxed whitespace-pre-wrap rounded-xl">
                {contact.why_contacting && <div className="font-semibold mb-1">{contact.why_contacting}</div>}
                {contact.message}
              </div>
            </div>
          )}

          {/* Running admin notes */}
          <AdminNotesEditor contact={contact} onUpdated={onAdminNotesUpdated} />

          {/* Recent email sends — only visible if there's history */}
          <EmailTimeline contactId={contact.id} refreshSignal={emailRefreshSignal} />

          {/* Auto-merged duplicate submissions — surfaces when the same
              person filled a second form (e.g. Form 33 quick + Form 17 full)
              and the backfill folded their richer data into this record.
              Visible only when there's history so the drawer stays clean. */}
          {Array.isArray(contact.merged_from_history) && contact.merged_from_history.length > 0 && (
            <div data-testid="merged-from-history">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Auto-merged submissions</div>
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2">
                {contact.merged_from_history.map((h, idx) => (
                  <div key={idx} className="text-xs text-stone-700">
                    <div className="flex items-start gap-2">
                      <span className="text-violet-600 font-bold">↳</span>
                      <div className="flex-1 min-w-0">
                        <div>
                          <span className="font-semibold">{h.loser_form_title || `Form ${h.loser_form_id || ""}`}</span>
                          {h.loser_gravity_entry_id && (
                            <span className="text-stone-500"> · entry #{h.loser_gravity_entry_id}</span>
                          )}
                        </div>
                        {h.merged_at && (
                          <div className="text-[10px] text-stone-500">Merged {new Date(h.merged_at).toLocaleString("en-GB")} · by {h.merged_by || "system"}</div>
                        )}
                        {Array.isArray(h.fields_added) && h.fields_added.length > 0 ? (
                          <div className="text-[10px] text-violet-700 mt-0.5">
                            Added: {h.fields_added.join(", ")}
                          </div>
                        ) : h.fields_added ? (
                          <div className="text-[10px] text-stone-500 mt-0.5">No new fields — submission logged for audit only.</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 10. Internal Notes */}
          {contact.notes && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Internal Notes</div>
              <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-stone-800 leading-relaxed whitespace-pre-wrap rounded-xl">{contact.notes}</div>
            </div>
          )}
        </div>
      </aside>
      <ReplyWithTemplateModal
        open={replyModalOpen}
        contact={contact}
        onClose={() => setReplyModalOpen(false)}
        onSent={() => setEmailRefreshSignal((n) => n + 1)}
      />
      {postcodeMapContact && (
        <ContactPostcodeMapModal
          contact={postcodeMapContact}
          onClose={() => setPostcodeMapContact(null)}
        />
      )}
    </div>
  );
}


function InterestedChecklist({ contact, onChanged }) {
  // The Interested stage tracks concrete actions:
  //  • Initial Territory Sent?  • Territory confirmed?
  //  • Contract Sent?
  //  • Shadow Day Booked?   (with date + structured host: HQ or an
  //    active franchisee — same fields the Discovery Portal reads so
  //    admins don't have to enter shadow-day info twice.)
  //  • Training Day(s) booked? (with one-or-more dates — training runs 2–3 days)
  const [initialTerritorySent, setInitialTerritorySent] = useState(!!contact.initial_territory_sent);
  const [territoryDefined, setTerritoryDefined] = useState(!!contact.territory_defined);
  const [contractSent, setContractSent] = useState(!!contact.contract_sent);
  const [shadowDayBooked, setShadowDayBooked] = useState(!!contact.shadow_day_booked);
  const [shadowDate, setShadowDate] = useState(contact.shadow_day_date || "");
  // Structured host — 'hq' | 'regional' | '' (unset). Discovery Portal
  // reads these exact fields (see `discovery_portal_routes._resolve_shadow_day`).
  const [shadowLocation, setShadowLocation] = useState((contact.shadow_day_location || "").toLowerCase());
  const [shadowFranchiseeId, setShadowFranchiseeId] = useState(contact.shadow_day_regional_franchisee_id || "");
  const [franchisees, setFranchisees] = useState([]);
  const [franchiseeQuery, setFranchiseeQuery] = useState("");
  const [trainingBooked, setTrainingBooked] = useState(!!contact.training_days_booked);
  const [trainingDates, setTrainingDates] = useState(Array.isArray(contact.training_day_dates) ? [...contact.training_day_dates] : []);
  const [newTrainingDate, setNewTrainingDate] = useState("");
  // Free-text location so admins can note HQ or a specific franchisee
  // venue for the training. The Discovery Portal pulls this into its
  // TRAINING DAYS section so prospects see the same location the
  // admin has entered here.
  const [trainingLocation, setTrainingLocation] = useState(contact.training_location || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load active franchisees once — matches the exact filter the
  // "Franchises / Licences" page's Active tab uses (see
  // ``FranchiseesPage.js`` ``SEGMENTS`` / ``isOther``): the record
  // must be tagged "Franchisee" and MUST NOT be tagged "Demo" or
  // "HQ" (those live in the "Other" tab and shouldn't appear as
  // shadow-day hosts). This is the same list Sam sees on the live
  // franchises page.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/franchisees", { params: { limit: 500 } });
        const list = Array.isArray(data) ? data : (data?.franchisees || data?.items || []);
        const hasTag = (f, t) => Array.isArray(f.tags) ? f.tags.includes(t) : f.tags === t;
        setFranchisees(
          list.filter((f) => hasTag(f, "Franchisee") && !hasTag(f, "Demo") && !hasTag(f, "HQ")),
        );
      } catch { /* leave empty */ }
    })();
  }, []);

  // When the drawer switches to a different contact, sync local state.
  useEffect(() => {
    setInitialTerritorySent(!!contact.initial_territory_sent);
    setTerritoryDefined(!!contact.territory_defined);
    setContractSent(!!contact.contract_sent);
    setShadowDayBooked(!!contact.shadow_day_booked);
    setShadowDate(contact.shadow_day_date || "");
    setShadowLocation((contact.shadow_day_location || "").toLowerCase());
    setShadowFranchiseeId(contact.shadow_day_regional_franchisee_id || "");
    setFranchiseeQuery("");
    setTrainingBooked(!!contact.training_days_booked);
    setTrainingDates(Array.isArray(contact.training_day_dates) ? [...contact.training_day_dates] : []);
    setTrainingLocation(contact.training_location || "");
    setNewTrainingDate("");
  }, [contact.id, contact.initial_territory_sent, contact.territory_defined, contact.contract_sent, contact.shadow_day_booked, contact.shadow_day_date, contact.shadow_day_location, contact.shadow_day_regional_franchisee_id, contact.training_days_booked, contact.training_day_dates, contact.training_location]);

  // Single saver — accepts overrides so callers don't have to wait for
  // setState before firing the PATCH.
  const persist = async (overrides = {}) => {
    setSaving(true); setError("");
    const payload = {
      initial_territory_sent: initialTerritorySent,
      territory_defined: territoryDefined,
      contract_sent: contractSent,
      shadow_day_booked: shadowDayBooked,
      shadow_day_date: shadowDate,
      shadow_day_location: shadowLocation || null,
      shadow_day_regional_franchisee_id: shadowLocation === "regional" ? (shadowFranchiseeId || null) : null,
      training_days_booked: trainingBooked,
      training_day_dates: trainingDates,
      training_location: trainingLocation,
      ...overrides,
    };
    try {
      const { data } = await api.patch(`/contacts/${contact.id}/checklist`, payload);
      onChanged?.({ id: contact.id, ...data });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save.");
      throw e;
    } finally { setSaving(false); }
  };

  const toggle = async (field, next, setter) => {
    const prev = next ? false : true;
    setter(next);
    try { await persist({ [field]: next }); }
    catch { setter(prev); }
  };

  const addTrainingDate = async () => {
    const d = (newTrainingDate || "").trim();
    if (!d) return;
    if (trainingDates.includes(d)) { setNewTrainingDate(""); return; }
    const next = [...trainingDates, d].sort();
    setTrainingDates(next);
    setNewTrainingDate("");
    try { await persist({ training_day_dates: next }); }
    catch { setTrainingDates(trainingDates); }
  };

  const removeTrainingDate = async (d) => {
    const next = trainingDates.filter((x) => x !== d);
    setTrainingDates(next);
    try { await persist({ training_day_dates: next }); }
    catch { setTrainingDates(trainingDates); }
  };

  const fmtTrain = (s) => {
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };

  const Tick = ({ k, label, checked, onCheck }) => (
    <label className="flex items-center gap-2 cursor-pointer hover:bg-blue-50/50 px-2 py-1.5 rounded-md transition-colors" data-testid={`interested-checklist-${k}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onCheck(e.target.checked)}
        disabled={saving}
        className="w-4 h-4 rounded border-stone-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
      />
      <span className={`text-sm ${checked ? "text-stone-500 line-through" : "text-stone-900 font-medium"}`}>{label}</span>
    </label>
  );

  return (
    <div className="border-2 border-blue-200 bg-blue-50/40 rounded-xl p-3" data-testid="interested-checklist">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-blue-700 mb-2 flex items-center justify-between px-1">
        <span>Checklist</span>
        {saving && <span className="text-[10px] font-normal normal-case text-stone-500">Saving…</span>}
      </div>

      {/* Top row — 3 primary ticks on one landscape row so the panel
          stays shallow instead of stacking checkboxes vertically. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
        <Tick k="initial-territory" label="Initial Territory Sent?" checked={initialTerritorySent} onCheck={(v) => toggle("initial_territory_sent", v, setInitialTerritorySent)} />
        <Tick k="territory" label="Territory confirmed?" checked={territoryDefined} onCheck={(v) => toggle("territory_defined", v, setTerritoryDefined)} />
        <Tick k="contract" label="Contract Sent?" checked={contractSent} onCheck={(v) => toggle("contract_sent", v, setContractSent)} />
      </div>

      {/* Divider between the top two ticks and the Shadow Day group so the
          three sub-sections (Top row / Shadow Day / Training Day(s)) all
          read as distinct steps. */}
      <div className="my-2 border-t border-blue-200/70" />

      {/* Shadow Day — landscape row: tick + date + shadowing type live
          on the SAME line so the checklist stays shallow. Sub-details
          (regional franchisee search) only render when needed. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="shrink-0">
          <Tick k="shadow" label="Shadow Day Booked?" checked={shadowDayBooked} onCheck={(v) => toggle("shadow_day_booked", v, setShadowDayBooked)} />
        </div>
        {shadowDayBooked && (
          <>
            <input
              type="date"
              value={shadowDate}
              onChange={(e) => setShadowDate(e.target.value)}
              onBlur={() => { if ((shadowDate || "") !== (contact.shadow_day_date || "")) persist().catch(() => {}); }}
              disabled={saving}
              data-testid="interested-shadow-date"
              className="px-2 py-1 bg-white border border-blue-200 rounded-lg text-xs focus:outline-none focus:border-blue-500"
            />
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-800 shrink-0">Shadowing:</span>
            <select
              value={shadowLocation || ""}
              onChange={(e) => {
                const next = e.target.value || "";
                setShadowLocation(next);
                const nextFid = next === "regional" ? shadowFranchiseeId : "";
                if (next !== "regional") setShadowFranchiseeId("");
                persist({
                  shadow_day_location: next || null,
                  shadow_day_regional_franchisee_id: nextFid || null,
                }).catch(() => {});
              }}
              disabled={saving}
              data-testid="interested-shadowing-host-type"
              className="flex-1 min-w-[10rem] px-2 py-1 bg-white border border-blue-200 rounded-lg text-xs focus:outline-none focus:border-blue-500"
            >
              <option value="">Choose host…</option>
              <option value="hq">HQ — Sandra Caldeira-Dunkerley</option>
              <option value="regional">Regional Franchisee</option>
            </select>
          </>
        )}
      </div>
      {shadowDayBooked && (
        <div className="px-2 pt-1 pb-2 space-y-2">
          {shadowLocation === "regional" && (
            <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-2 items-start">
              <span className="text-xs font-bold uppercase tracking-wider text-blue-800 shrink-0 sm:pt-2">Franchisee:</span>
              <div>
                {/* Currently selected summary — gives clear confirmation
                    that the click landed, since a picker without this
                    feels 'inert' after a selection (the listbox
                    highlight alone isn't obviously "saved"). */}
                {(() => {
                  const sel = franchisees.find((f) => f.id === shadowFranchiseeId);
                  if (!sel) return (
                    <div className="mb-1.5 text-xs text-stone-500" data-testid="interested-shadowing-franchisee-none">
                      No franchisee selected yet.
                    </div>
                  );
                  const name = [sel.first_name, sel.last_name].filter(Boolean).join(" ").trim()
                             || sel.organisation || sel.mojo_email || sel.id;
                  const num = sel.franchise_number ? ` · #${sel.franchise_number}` : "";
                  const city = sel.city ? ` · ${sel.city}` : "";
                  return (
                    <div
                      className="relative z-10 mb-1.5 inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-blue-100 text-blue-900 text-xs font-semibold border border-blue-200"
                      data-testid="interested-shadowing-franchisee-selected"
                    >
                      <span className="inline-block w-2 h-2 rounded-full bg-blue-600" />
                      Shadowing with: {name}{num}{city}
                      <button
                        type="button"
                        onClick={() => {
                          setShadowFranchiseeId("");
                          persist({ shadow_day_regional_franchisee_id: null }).catch(() => {});
                        }}
                        className="ml-1 text-blue-700 hover:text-blue-900 text-[10px] font-bold uppercase tracking-wider underline"
                        data-testid="interested-shadowing-franchisee-clear"
                      >
                        Clear
                      </button>
                    </div>
                  );
                })()}
                <input
                  type="search"
                  value={franchiseeQuery}
                  onChange={(e) => setFranchiseeQuery(e.target.value)}
                  placeholder="Search active franchisees…"
                  data-testid="interested-shadowing-franchisee-search"
                  className="w-full mb-1 px-3 py-1.5 bg-white border border-blue-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
                {/* Button list — one row per active franchisee. Uses
                    an aria-selected style so the selected franchisee
                    stays visibly locked-in even after the drawer
                    re-renders (no ambiguous "did it save?" state). */}
                <div
                  role="listbox"
                  data-testid="interested-shadowing-franchisee-list"
                  className="w-full max-h-52 overflow-y-auto bg-white border border-blue-200 rounded-lg divide-y divide-blue-100"
                >
                  {franchisees.length === 0 && (
                    <div className="px-3 py-2 text-sm text-stone-400">Loading active franchisees…</div>
                  )}
                  {franchisees
                    .filter((f) => {
                      const q = franchiseeQuery.trim().toLowerCase();
                      if (!q) return true;
                      const label = [f.first_name, f.last_name, f.organisation, f.franchise_number, f.mojo_email, f.city, f.postcode]
                        .filter(Boolean).join(" ").toLowerCase();
                      return label.includes(q);
                    })
                    .map((f) => {
                      const name = [f.first_name, f.last_name].filter(Boolean).join(" ").trim() || f.organisation || f.mojo_email || f.id;
                      const num = f.franchise_number ? ` · #${f.franchise_number}` : "";
                      const city = f.city ? ` · ${f.city}` : "";
                      const isSelected = f.id === shadowFranchiseeId;
                      return (
                        <button
                          key={f.id}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          disabled={saving}
                          onClick={() => {
                            setShadowFranchiseeId(f.id);
                            persist({ shadow_day_regional_franchisee_id: f.id }).catch(() => {});
                          }}
                          data-testid={`interested-shadowing-franchisee-option-${f.id}`}
                          className={
                            "block w-full text-left px-3 py-1.5 text-sm transition-colors " +
                            (isSelected
                              ? "bg-blue-600 text-white font-semibold"
                              : "text-stone-900 hover:bg-blue-50")
                          }
                        >
                          {name}{num}{city}
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>
          )}
          {/* Feb-2026 (Sandra): once BOTH the shadow day date AND the
              shadow host are saved, HQ can open a pre-filled mailto:
              draft in their own email client. The draft is composed
              entirely client-side and NOT sent automatically — it
              opens as a draft in Outlook / Apple Mail / the user's
              default handler for the admin to review before sending.
              Nothing else is touched: the confidentiality agreement
              is signed exclusively via the public signing form gated
              by the prospect's Discovery Portal token, so opening
              this draft never marks it as signed. */}
          <ShadowDayConfirmMailto
            contact={contact}
            shadowDate={shadowDate}
            shadowLocation={shadowLocation}
            shadowFranchisee={franchisees.find((f) => f.id === shadowFranchiseeId) || null}
          />
        </div>
      )}

      {/* Divider between Shadow Day and Training Day(s) so they read as
          two clearly separate steps. */}
      <div className="my-2 border-t border-blue-200/70" />

      {/* Training Day(s) — landscape row: tick + date add + location on
          one line. When NOT ticked the row collapses to just the tick,
          so no vertical space is reserved by unused controls. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="shrink-0">
          <Tick k="training" label="Training Day(s) booked?" checked={trainingBooked} onCheck={(v) => toggle("training_days_booked", v, setTrainingBooked)} />
        </div>
        {trainingBooked && (
          <>
            <input
              type="date"
              value={newTrainingDate}
              onChange={(e) => setNewTrainingDate(e.target.value)}
              disabled={saving}
              data-testid="interested-training-date-input"
              className="px-2 py-1 bg-white border border-blue-200 rounded-lg text-xs focus:outline-none focus:border-blue-500"
            />
            <button
              type="button"
              onClick={addTrainingDate}
              disabled={saving || !newTrainingDate}
              data-testid="interested-training-date-add"
              className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-40 flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add date
            </button>
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-800 shrink-0">Location:</span>
            <input
              type="text"
              value={trainingLocation}
              onChange={(e) => setTrainingLocation(e.target.value)}
              onBlur={() => {
                if ((trainingLocation || "") !== (contact.training_location || "")) {
                  persist().catch(() => {});
                }
              }}
              disabled={saving}
              placeholder="HQ, or franchisee venue"
              data-testid="interested-training-location"
              className="flex-1 min-w-[10rem] px-2 py-1 bg-white border border-blue-200 rounded-lg text-xs focus:outline-none focus:border-blue-500"
            />
          </>
        )}
      </div>
      {trainingBooked && trainingDates.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="interested-training-date-list">
          {trainingDates.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 text-[11px] font-semibold bg-white border border-blue-300 text-blue-900 rounded-full">
              {fmtTrain(d)}
              <button
                type="button"
                onClick={() => removeTrainingDate(d)}
                disabled={saving}
                data-testid={`interested-training-date-remove-${d}`}
                className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-blue-100 text-blue-700">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Placeholder to swallow the original wrapper's remaining
          markup — see below (kept for backwards-compat lint). */}

      {error && <div className="text-xs text-red-700 mt-2 px-1">{error}</div>}
    </div>
  );
}

function AdminNotesEditor({ contact, onUpdated, fullHeight = false }) {
  // Local draft separates the input value from the persisted value so the
  // user can type freely without losing focus mid-write. Auto-saves on blur
  // (with a short debounce safety net so accidental clicks don't lose work).
  const [draft, setDraft] = useState(contact.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(contact.admin_notes_updated_at || null);
  const [error, setError] = useState("");
  const initialRef = contact.admin_notes || "";

  useEffect(() => {
    // When the drawer switches to a new contact, reset the draft.
    setDraft(contact.admin_notes || "");
    setSavedAt(contact.admin_notes_updated_at || null);
    setError("");
  }, [contact.id, contact.admin_notes, contact.admin_notes_updated_at]);

  const dirty = draft !== initialRef;

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch(`/contacts/${contact.id}/admin-notes`, { admin_notes: draft });
      setSavedAt(data.admin_notes_updated_at);
      onUpdated && onUpdated(contact.id, data.admin_notes, data.admin_notes_updated_at);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save notes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-notes-editor" className={fullHeight ? "flex flex-col h-full min-h-0 w-full" : undefined}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Notes</div>
        <div className="text-[10px] text-stone-500 flex items-center gap-1.5">
          {saving ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" /> Saving…
            </>
          ) : dirty ? (
            <span className="text-amber-700">Unsaved changes</span>
          ) : savedAt ? (
            <span title={`Last saved ${savedAt}`}>
              Saved {(() => { const d = new Date(savedAt); const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000)); if (diff < 60) return "just now"; if (diff < 3600) return `${Math.floor(diff / 60)}m ago`; if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`; return d.toLocaleDateString("en-GB"); })()}
            </span>
          ) : (
            <span className="text-stone-400">Type to add notes</span>
          )}
        </div>
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        placeholder="Running notes — follow-ups, call summaries, anything you want to remember about this contact…"
        rows={fullHeight ? undefined : 4}
        data-testid="admin-notes-textarea"
        className={
          fullHeight
            ? "flex-1 min-h-0 w-full px-3 py-2 bg-white border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-900 resize-none"
            : "w-full px-3 py-2 bg-white border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-900 resize-y"
        }
      />
      <div className="flex items-center justify-between mt-1.5">
        <div className="text-[10px] text-stone-500">
          {dirty
            ? "Click outside or press ⌘/Ctrl + Enter to save."
            : draft.length > 0
              ? `${draft.length} characters`
              : ""}
        </div>
        {dirty && (
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="admin-notes-save"
            className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-900 hover:text-stone-950 underline disabled:opacity-40">
            Save now
          </button>
        )}
      </div>
      {error && (
        <div className="mt-1.5 text-xs text-red-700 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </div>
      )}
    </div>
  );
}

export default function ContactsPage() {
  const navigate = useNavigate();
  // Read one-shot query params from the URL for deep-linking (e.g. the
  // Dashboard Journey rows link to `/contacts?tab=tabs&expand=<id>` and
  // expect the Sales Pipeline Tabs view to open with that contact's
  // row auto-expanded). We consume them once on mount so a manual
  // click elsewhere doesn't re-trigger the deep-link.
  const initialQuery = useMemo(() => {
    if (typeof window === "undefined") return { tabParam: null, expandId: null };
    const params = new URLSearchParams(window.location.search);
    return {
      tabParam: params.get("tab"),
      expandId: params.get("expand"),
    };
  }, []);
  const [tab, setTab] = useState("pipeline");
  const [view, setView] = useState(initialQuery.tabParam === "tabs" ? "list" : "list");
  // Contact id whose Tabs-view row should be auto-expanded once the
  // pipeline data loads. Cleared after being consumed by the child so
  // the user can freely collapse/expand other rows without it snapping
  // back on the next render.
  const [initialExpandedId, setInitialExpandedId] = useState(initialQuery.expandId || null);
  // Auto-temperature score map (contact_id → {score, band}) for kanban
  // cards. Loaded once when the pipeline view opens and refreshed when
  // the user toggles "Sort by hot lead". Empty map = render no badge.
  const [tempMap, setTempMap] = useState({});
  // When on, kanban columns sort by AUTO score desc (hottest first).
  const [sortByHot, setSortByHot] = useState(false);
  // Header expand/collapse toggle — hides the tab-picker cards + the
  // pipeline filter/stats block by default so the TABS list view stays
  // clean. Persisted in localStorage so admin choice sticks across
  // sessions. Default collapsed (false).
  const [headerExpanded, setHeaderExpanded] = useState(() => {
    try { return window.localStorage.getItem("cm.contacts.headerExpanded") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("cm.contacts.headerExpanded", headerExpanded ? "1" : "0"); }
    catch { /* localStorage disabled — silently ignore */ }
  }, [headerExpanded]);

  useEffect(() => {
    if (tab !== "pipeline") return;
    let cancelled = false;
    api.get("/pipeline/temperatures")
      .then(({ data }) => { if (!cancelled) setTempMap(data.temperatures || {}); })
      .catch(() => { /* silent — sort still works with score=0 */ });
    return () => { cancelled = true; };
  }, [tab]);
  const [stageFilter, setStageFilter] = useState("");
  const [search, setSearch] = useState("");
  const [recentSearches, setRecentSearches] = useState(() => loadRecentSearches());
  const [searchFocused, setSearchFocused] = useState(false);
  const [data, setData] = useState({ items: [], total: 0 });
  const [counts, setCounts] = useState({});
  // Display cap for the LIST view — chunked 500 at a time so we don't shove
  // 6k+ DOM rows in at once. "Show 500 more" extends until the dataset is
  // fully visible. Reset whenever the dataset (tab / search / stage) changes.
  const [displayLimit, setDisplayLimit] = useState(500);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lastSelectedId, setLastSelectedId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // "Export CSV" button state — Feb 2026 lean download of the
  // currently-filtered Franchise Contacts view. Mirrors the same
  // filter query params as the list endpoint so what you see in the
  // table is what lands in the CSV.
  const [exportBusy, setExportBusy] = useState(false);
  const exportContactsCsv = async () => {
    if (exportBusy) return;
    setExportBusy(true);
    try {
      const params = new URLSearchParams();
      if (tab) params.set("tab", tab);
      if (search && search.trim()) params.set("search", search.trim());
      if (stageFilter) params.set("pipeline_status", stageFilter);
      params.set("limit", "10000");
      const { data } = await api.get(`/contacts/export.csv?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.download = `franchise-contacts-${tab || "all"}-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export ready — check your downloads.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV export failed. Please try again.");
    } finally {
      setExportBusy(false);
    }
  };
  // Contact whose Reply-with-template modal is currently open from the kanban
  // quick-Reply button (not the drawer — the drawer has its own state).
  const [kanbanReplyContact, setKanbanReplyContact] = useState(null);
  // Bulk-reply: array of full contact docs handed to the shared
  // ReplyWithTemplateModal in bulk mode. Populated from selectedIds
  // when the admin taps "Bulk Reply" in the bulk-action bar. Kept as
  // a snapshot so mutations to the underlying selection (e.g. Clear)
  // don't yank the recipient list out from under an open modal.
  const [bulkReplyContacts, setBulkReplyContacts] = useState(null);
  // Contact whose postcode map modal is currently open from the pipeline
  // tabs view (the drawer has its own state for the same modal).
  const [postcodeMapContact, setPostcodeMapContact] = useState(null);
  const [ageFilter, setAgeFilter] = useState("all");
  // Date-range filter — sits alongside the AGE tier chips on the pipeline
  // header. When both ``from`` and ``to`` are blank the filter is a
  // no-op and the AGE chip drives visibleItems on its own. Setting
  // either side narrows visibleItems to contacts whose ``date`` (or
  // ``date_added``) falls inside the inclusive range. Feb 2026: Sam's
  // brief — needs to work across every stage (NEW / CONTACTED /
  // FOLLOW-UP DUE / INTERESTED / DORMANT / LOST) and both views (TABS +
  // PIPELINE) since visibleItems feeds both.
  const [dateRangeFilter, setDateRangeFilter] = useState({ from: "", to: "" });
  // Care Home Contacts tab — map view + filters.
  // Presets: 30d / 12m / custom (with from+to inputs) / all.
  // franchiseeId: "" = all, "uncovered" = pins outside every territory,
  // or a specific franchisee ID to scope to their territory.
  const [careHomeFilter, setCareHomeFilter] = useState({
    preset: "30d",
    from: "",
    to: "",
    franchiseeId: "",
  });
  const [careHomeMap, setCareHomeMap] = useState({ pins: [], loading: false, error: "" });
  const [careHomeOverlay, setCareHomeOverlay] = useState({ franchisees: [], geojson: null, outlines: null });
  // Radius filter — user clicks a point on the map (or types a
  // postcode/lat/lng) to drop a circle; only pins inside land in the
  // list. Kept null when no radius filter is active.
  const [careHomeRadius, setCareHomeRadius] = useState(null); // { lat, lng, miles }
  const [radiusMode, setRadiusMode] = useState(false); // when true, next map click places/moves the circle
  // Hover-linked highlight: as the admin mouses over a Care Home row
  // the corresponding pin enlarges + brightens on the map so they can
  // spot the geography at a glance. Cleared on mouse-leave.
  const [hoveredContactId, setHoveredContactId] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all"); // 'all' | 'franchise' | 'licence'
  // Follow-up email filter (Feb 2026). 'all' shows everyone,
  // 'sent' shows contacts who've received at least one follow-up
  // email, 'not_sent' shows contacts who haven't. Works alongside
  // (not in place of) the existing sortByHot toggle + source filter.
  const [followUpFilter, setFollowUpFilter] = useState("all"); // 'all' | 'sent' | 'not_sent'
  // When the toolbar "Follow-up Due · N waiting" chip is clicked we
  // set this to ``"follow_up_due"`` so the useEffect below scrolls the
  // right column into view + briefly highlights it. Cleared on any
  // other pipeline action (drag / stage move / clicking elsewhere).
  const [focusStage, setFocusStage] = useState(null);
  const [collapsedStages, setCollapsedStages] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("pipelineCollapsedStages") || "[]")); }
    catch { return new Set(); }
  });
  const toggleStageCollapsed = (key) => {
    setCollapsedStages((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem("pipelineCollapsedStages", JSON.stringify([...next])); } catch {/* ignore */}
      return next;
    });
  };

  const clearSelection = () => { setSelectedIds(new Set()); setLastSelectedId(null); };

  // When the toolbar "Follow-up Due · N waiting" chip is pressed we
  // set ``focusStage`` and switch to pipeline view. This effect
  // (1) makes sure that column isn't collapsed, (2) scrolls it into
  // view, and (3) auto-clears the ring after 2.5s so the highlight
  // is a nudge, not a persistent state.
  useEffect(() => {
    if (!focusStage) return;
    // Expand the target column if it happens to be collapsed.
    if (collapsedStages.has(focusStage)) {
      setCollapsedStages((prev) => {
        const next = new Set(prev);
        next.delete(focusStage);
        try { localStorage.setItem("pipelineCollapsedStages", JSON.stringify([...next])); } catch {/* ignore */}
        return next;
      });
    }
    const t1 = setTimeout(() => {
      const el = document.querySelector(`[data-testid="pipeline-column-${focusStage}"]`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      }
    }, 60);
    const t2 = setTimeout(() => setFocusStage(null), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [focusStage, collapsedStages]);
  // Visible items (filtered + age + source) — needed so shift-select knows the range.
  // Default order: newest "contacted on" date at the top, oldest at the bottom.
  // Cards without a date sink to the bottom (rare — usually means an import gap).
  const visibleItems = useMemo(() => {
    const af = AGE_TIERS.find((t) => t.key === ageFilter) || AGE_TIERS[0];
    // Precompute the date-range bounds once so the per-item filter
    // below stays cheap. Empty from/to → the range check is a no-op.
    const fromTs = dateRangeFilter.from
      ? Date.parse(`${dateRangeFilter.from}T00:00:00`)
      : null;
    const toTs = dateRangeFilter.to
      ? Date.parse(`${dateRangeFilter.to}T23:59:59.999`)
      : null;
    const filtered = data.items.filter((c) => {
      if (!af.test(daysSince(c.date || c.date_added))) return false;
      if (sourceFilter === "franchise" && c.source !== "franchise_enquiry") return false;
      if (sourceFilter === "licence"   && c.source !== "licence_enquiry")   return false;
      const followUpCount = Number(c.follow_up_sent_count || 0);
      if (followUpFilter === "sent" && followUpCount < 1) return false;
      if (followUpFilter === "not_sent" && followUpCount >= 1) return false;
      if (fromTs != null || toTs != null) {
        const raw = c.date || c.date_added;
        const t = raw ? Date.parse(raw) : NaN;
        if (!Number.isFinite(t)) return false;
        if (fromTs != null && t < fromTs) return false;
        if (toTs != null && t > toTs) return false;
      }
      return true;
    });
    const ts = (c) => {
      const d = c.date || c.date_added;
      if (!d) return -Infinity;
      const t = Date.parse(d);
      return Number.isFinite(t) ? t : -Infinity;
    };
    return filtered.sort((a, b) => ts(b) - ts(a));
  }, [data.items, ageFilter, sourceFilter, followUpFilter, dateRangeFilter]);

  // Care Home filter: resolve preset → concrete date range (ISO YYYY-MM-DD).
  const careHomeRange = useMemo(() => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (careHomeFilter.preset === "all") return { from: "", to: "" };
    if (careHomeFilter.preset === "custom") return { from: careHomeFilter.from || "", to: careHomeFilter.to || "" };
    const back = careHomeFilter.preset === "12m" ? 365 : 30;
    const from = new Date(today);
    from.setDate(from.getDate() - back);
    return { from: iso(from), to: iso(today) };
  }, [careHomeFilter]);

  // Fetch map pins whenever the Care Home tab filter changes. Also
  // loads the franchisee territory overlay so pins draw against the
  // full UK coverage map (same look as Territory Builder).
  useEffect(() => {
    if (tab !== "care_home") return;
    let alive = true;
    setCareHomeMap((m) => ({ ...m, loading: true, error: "" }));
    (async () => {
      try {
        const params = { source: "care_home_enquiry" };
        if (careHomeRange.from) params.date_from = careHomeRange.from;
        if (careHomeRange.to) params.date_to = careHomeRange.to;
        if (careHomeFilter.franchiseeId) params.franchisee_id = careHomeFilter.franchiseeId;
        const [mapResp, overlayResp] = await Promise.all([
          api.get("/contacts/map", { params }),
          careHomeOverlay.geojson
            ? Promise.resolve({ data: careHomeOverlay })
            : api.get("/territory/all-franchisees"),
        ]);
        if (!alive) return;
        setCareHomeMap({ pins: mapResp.data.pins || [], loading: false, error: "" });
        if (!careHomeOverlay.geojson) {
          setCareHomeOverlay({
            franchisees: overlayResp.data.franchisees || [],
            geojson: overlayResp.data.geojson || null,
            outlines: overlayResp.data.outlines || null,
          });
        }
      } catch {
        if (alive) setCareHomeMap((m) => ({ ...m, loading: false, error: "Couldn't load the map." }));
      }
    })();
    return () => { alive = false; };
  }, [tab, careHomeRange.from, careHomeRange.to, careHomeFilter.franchiseeId]);

  // Client-side reconciliation of the LEFT list with the map filters
  // so the list only shows what's plotted. Rows without postcodes are
  // hidden when a date/franchisee filter is active — they'd be silent
  // outliers otherwise. When the filter is "all + no franchisee", we
  // fall back to the full visibleItems (no map filtering applied).
  const careHomePinsInRadius = useMemo(() => {
    if (!careHomeRadius) return careHomeMap.pins;
    const { lat: cLat, lng: cLng, miles } = careHomeRadius;
    const R = 3958.7613;  // earth radius in miles
    const toRad = (d) => (d * Math.PI) / 180;
    return careHomeMap.pins.filter((p) => {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return false;
      const dLat = toRad(p.lat - cLat);
      const dLng = toRad(p.lng - cLng);
      const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(cLat)) * Math.cos(toRad(p.lat)) * Math.sin(dLng / 2) ** 2;
      const d = 2 * R * Math.asin(Math.sqrt(a));
      return d <= miles;
    });
  }, [careHomeMap.pins, careHomeRadius]);
  const careHomePinIds = useMemo(() => new Set(careHomePinsInRadius.map((p) => p.id)), [careHomePinsInRadius]);
  const listItems = useMemo(() => {
    if (tab !== "care_home") return visibleItems;
    const filterActive = careHomeFilter.preset !== "all" || !!careHomeFilter.franchiseeId || !!careHomeRadius;
    if (!filterActive) return visibleItems;
    if (careHomeMap.error) return visibleItems;
    if (careHomeMap.loading && careHomePinIds.size === 0 && !careHomeRadius) return visibleItems;
    return visibleItems.filter((c) => careHomePinIds.has(c.id));
  }, [tab, visibleItems, careHomePinIds, careHomeFilter, careHomeMap.error, careHomeMap.loading, careHomeRadius]);

  const toggleSelect = (id, evt) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      // Shift-click: extend the live selection from the anchor (last clicked checkbox)
      // up to the current one. Only triggers when the anchor is still selected — otherwise
      // fall through to single-toggle. In the kanban view the range must be scoped to
      // a SINGLE stage so a shift-click in NEW doesn't pull cards in from INTERESTED /
      // TERRITORY MAP etc. (visibleItems otherwise interleaves stages).
      if (evt && evt.shiftKey && lastSelectedId && lastSelectedId !== id && next.has(lastSelectedId)) {
        let rangeItems = visibleItems;
        if (view === "pipeline") {
          const anchor = visibleItems.find((c) => c.id === lastSelectedId);
          const target = visibleItems.find((c) => c.id === id);
          const anchorStage = (anchor?.pipeline_status && STAGE_MAP[anchor.pipeline_status]) ? anchor.pipeline_status : "new";
          const targetStage = (target?.pipeline_status && STAGE_MAP[target.pipeline_status]) ? target.pipeline_status : "new";
          if (anchorStage !== targetStage) {
            // Cross-column shift — refuse to extend; fall through to single toggle.
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          }
          rangeItems = (grouped[anchorStage] || []);
        }
        const ids = rangeItems.map((c) => c.id);
        const a = ids.indexOf(lastSelectedId);
        const b = ids.indexOf(id);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          // Anchor is selected — ADD the range to the selection.
          for (let i = lo; i <= hi; i++) next.add(ids[i]);
          return next;
        }
      }
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setLastSelectedId(id);
  };

  const load = async (signal) => {
    setLoading(true);
    try {
      const params = { tab, search: search || undefined, limit: 10000 };
      if (tab === "pipeline" && stageFilter) params.pipeline_status = stageFilter;
      const { data } = await api.get("/contacts", { params, signal });
      if (!signal || !signal.aborted) setData(data);
    } catch (e) {
      // Ignore Axios cancellation errors — they're expected when the
      // user re-triggers a load before the previous one finished.
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || e?.name === "AbortError") return;
      setError("Could not load contacts.");
    } finally {
      if (!signal || !signal.aborted) setLoading(false);
    }
  };

  // Open a contact drawer AND mark it as seen so its row un-bolds and the
  // red "new" tab badge decrements. Only fires the mark-seen API call if
  // the contact was actually unseen — avoids a wasted round-trip every
  // time someone re-opens the drawer. Pipeline contacts use a different
  // "new" signal (pipeline_status) so we skip mark-seen for them.
  const openContact = (c) => {
    setSelected(c);
    if (!c || c.seen_at || c.in_pipeline) return;
    // Optimistic local update so bolding disappears immediately.
    setData((d) => ({
      ...d,
      items: d.items.map((it) => it.id === c.id ? { ...it, seen_at: "pending" } : it),
    }));
    api.post(`/contacts/${c.id}/mark-seen`)
      .then(() => { loadCounts(); })
      .catch(() => {
        // Roll back the optimistic tick if the call failed.
        setData((d) => ({
          ...d,
          items: d.items.map((it) => it.id === c.id ? { ...it, seen_at: null } : it),
        }));
      });
  };

  // Tab-header badges: total live records per category. Reloads whenever a
  // contact is added / merged / moved so the numbers stay accurate.
  const loadCounts = async () => {
    try {
      const { data } = await api.get("/contacts/counts");
      setCounts(data || {});
    } catch (e) { /* badges are non-critical, swallow */ }
  };

  // Sales & Contacts data-freshness policy (Option A, Feb 2026 — Paul):
  //
  //   * NO recurring background refetch of the contact list. The 60s
  //     auto-refresh was clobbering in-flight edits and reshuffling the
  //     list under the user; removed. The sidebar new-enquiry badge in
  //     Layout.js still polls independently on its own cadence, so
  //     inbound leads are still surfaced without a manual reload.
  //   * Fresh fetch on ENTERING the page — handled by the mount
  //     useEffect below (fires when tab/stageFilter/search change,
  //     including initial mount).
  //   * Fresh fetch on TAB SWITCH — same mount useEffect.
  //   * Fresh fetch on BROWSER-FOCUS RETURN — visibilitychange handler
  //     below. If a contact drawer is open, the refetch is deferred
  //     (a "pending" flag is set) and fires the moment the drawer
  //     closes. This keeps the open contact and any unsaved edits
  //     intact.
  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);
  const pendingFocusRefetchRef = useRef(false);

  const refetchListAndCounts = useCallback(() => {
    loadCounts();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Browser-focus refetch — only when no drawer is open. When the
  // drawer is open, defer to drawer-close (see effect below).
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) return;
      if (selectedRef.current) {
        pendingFocusRefetchRef.current = true;
        return;
      }
      refetchListAndCounts();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refetchListAndCounts]);

  // Fire the deferred focus refetch the moment the drawer closes.
  useEffect(() => {
    if (selected) return;
    if (!pendingFocusRefetchRef.current) return;
    pendingFocusRefetchRef.current = false;
    refetchListAndCounts();
  }, [selected, refetchListAndCounts]);

  useEffect(() => {
    // AbortController prevents stale in-flight loads (from an earlier tab
    // / search / stageFilter) from clobbering fresher state — the exact
    // race that intermittently blanked the page (Feb 2026 RCA).
    const controller = new AbortController();
    const t = setTimeout(() => {
      load(controller.signal);
      const q = (search || "").trim();
      if (q.length >= 2) {
        saveRecentSearch(q);
        setRecentSearches(loadRecentSearches());
      }
    }, search ? 350 : 0);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, stageFilter, search]);

  // Counts are cheap — refresh whenever any mutation changes the dataset.
  useEffect(() => {
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, data.items.length]);

  // Reset selection whenever the tab/filter changes
  useEffect(() => { clearSelection(); setAgeFilter("all"); setSourceFilter("all"); setDateRangeFilter({ from: "", to: "" }); setDisplayLimit(500); }, [tab, stageFilter, search]);

  // Listen for auto-advance events fired by ReplyWithTemplateModal
  // (backend moves a "new" contact to "contacted" when a templated
  // email is sent). We update local state so the card moves columns
  // immediately without a full reload.
  useEffect(() => {
    const handler = (e) => {
      const { contactId, newStage } = e.detail || {};
      if (!contactId || !newStage) return;
      setData((d) => ({
        ...d,
        items: d.items.map((c) => (c.id === contactId
          ? { ...c, pipeline_status: newStage, in_pipeline: true }
          : c)),
      }));
      setSelected((sel) => sel && sel.id === contactId
        ? { ...sel, pipeline_status: newStage, in_pipeline: true }
        : sel);
    };
    window.addEventListener("pipeline:stage-changed", handler);
    return () => window.removeEventListener("pipeline:stage-changed", handler);
  }, []);

  const moveContact = async (contactId, target, pipeline_status) => {
    try {
      await api.post(`/contacts/${contactId}/move`, { target, pipeline_status });
      const stayingOnPipeline = target === "pipeline" && tab === "pipeline";
      if (stayingOnPipeline) {
        // Just changing stage — keep the row visible, update in place
        setData((d) => ({ ...d, items: d.items.map((c) => c.id === contactId
          ? { ...c, in_pipeline: true, pipeline_status: pipeline_status || "new" }
          : c) }));
        setSelected((sel) => sel && sel.id === contactId ? { ...sel, in_pipeline: true, pipeline_status: pipeline_status || "new" } : sel);
      } else {
        setSelected(null);
        setData((d) => ({ ...d, items: d.items.filter((c) => c.id !== contactId) }));
      }
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(contactId); return n; });
    } catch (e) { setError("Could not move contact."); }
  };

  // Reclassify a contact's enquiry TYPE (Franchise ↔ Licence ↔ General)
  // without removing them from the pipeline. Backed by the same /move
  // endpoint but keeps the row in place so the pipeline tabs stay
  // in-context — mirrors the drawer behaviour of "Change type".
  const changeContactSource = async (c, target) => {
    const labelMap = { franchise: "Franchise enquiry", licence: "Licence enquiry", general: "General enquiry" };
    if (!window.confirm(`Reclassify ${[c.first_name, c.last_name].filter(Boolean).join(" ") || "this contact"} as a ${labelMap[target]}?`)) return;
    try {
      await api.post(`/contacts/${c.id}/move`, { target });
      const newSource = target === "franchise" ? "franchise_enquiry" : target === "licence" ? "licence_enquiry" : "general_enquiry";
      setData((d) => ({
        ...d,
        items: d.items.map((x) => x.id === c.id ? { ...x, source: newSource } : x),
      }));
      setSelected((sel) => sel && sel.id === c.id ? { ...sel, source: newSource } : sel);
      loadCounts();
    } catch { setError("Could not reclassify contact."); }
  };

  const bulkMove = async (target, pipeline_status) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const stayingOnPipeline = target === "pipeline" && tab === "pipeline";
    const targetLabel = target === "pipeline" ? "Sales Pipeline"
                      : target === "franchise" ? "Franchise Contacts"
                      : target === "licence"   ? "Licence Contacts"
                      : "General Contacts";
    const stageLabel = pipeline_status ? (STAGE_MAP[pipeline_status]?.label || pipeline_status) : null;
    const verb = stayingOnPipeline ? "Change stage to" : "Move";
    const dest = stayingOnPipeline ? stageLabel : (stageLabel ? `${targetLabel} (${stageLabel})` : targetLabel);
    if (!window.confirm(`${verb} ${ids.length} contact${ids.length === 1 ? "" : "s"} → ${dest}?`)) return;
    try {
      await api.post(`/contacts/bulk-move`, { ids, target, pipeline_status });
      if (stayingOnPipeline) {
        // Records stay on this tab — just update their stage in-place
        setData((d) => ({ ...d, items: d.items.map((c) => selectedIds.has(c.id)
          ? { ...c, in_pipeline: true, pipeline_status: pipeline_status || "new" }
          : c) }));
      } else {
        // Records leave the current tab — remove them from the visible list
        setData((d) => ({ ...d, items: d.items.filter((c) => !selectedIds.has(c.id)) }));
      }
      clearSelection();
    } catch (e) { setError("Could not move contacts."); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} selected contact${ids.length === 1 ? "" : "s"}?\n\nThe Gravity Forms entries will be tombstoned so the hourly backfill won't re-import them. This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/contacts/bulk-delete", { ids });
      setData((d) => ({ ...d, items: d.items.filter((c) => !selectedIds.has(c.id)) }));
      clearSelection();
      loadCounts();
      // Surface a tiny success line so the admin sees what landed.
      setError("");
      if (typeof window !== "undefined") {
        window.setTimeout(() => {
          window.alert(`Deleted ${data?.deleted || 0} contact${(data?.deleted || 0) === 1 ? "" : "s"}.${data?.tombstoned ? ` Tombstoned ${data.tombstoned} Gravity Forms entr${data.tombstoned === 1 ? "y" : "ies"} so they won't reappear.` : ""}`);
        }, 50);
      }
    } catch (e) { setError("Could not delete contacts."); }
  };

  const updateStage = async (contactId, newStage) => {
    try {
      await api.patch(`/contacts/${contactId}/pipeline`, { pipeline_status: newStage });
      setData((d) => ({ ...d, items: d.items.map((c) => (c.id === contactId ? { ...c, pipeline_status: newStage } : c)) }));
      setSelected((sel) => sel && sel.id === contactId ? { ...sel, pipeline_status: newStage } : sel);
      // Nudge the sidebar "new leads" badge to re-fetch — Layout listens
      // for this and decrements as soon as a NEW lead is triaged.
      window.dispatchEvent(new CustomEvent("pipeline:stage-changed", { detail: { contactId, newStage } }));
    } catch (e) { /* noop */ }
  };

  // Retroactive "already sent the follow-up email" flag — for contacts
  // HQ replied to BEFORE the 7-day automated Follow-up Due workflow
  // rolled out. Stamps ``follow_up_sent_count = 1`` +
  // ``auto_followed_up_at`` so the scheduler permanently skips them,
  // and if the card happens to be in ``follow_up_due`` right now, the
  // backend flips it back to ``contacted``.
  const markFollowUpSent = async (contactId) => {
    try {
      const { data: res } = await api.post(`/contacts/${contactId}/mark-follow-up-sent`);
      const patch = {
        follow_up_sent_count: res.follow_up_sent_count,
        auto_followed_up_at: res.auto_followed_up_at,
      };
      if (res.pipeline_status) patch.pipeline_status = res.pipeline_status;
      setData((d) => ({ ...d, items: d.items.map((c) => (c.id === contactId ? { ...c, ...patch } : c)) }));
      setSelected((sel) => sel && sel.id === contactId ? { ...sel, ...patch } : sel);
    } catch (e) {
      setError("Could not mark follow-up as sent.");
    }
  };

  // Optimistically set the pipeline lead-temperature on a contact and
  // PATCH the server. Used from the kanban card; the drawer shows the
  // result read-only.
  const setTemperature = async (contactId, nextTemperature) => {
    const prev = data.items.find((c) => c.id === contactId)?.temperature ?? null;
    setData((d) => ({ ...d, items: d.items.map((c) => c.id === contactId ? { ...c, temperature: nextTemperature } : c) }));
    setSelected((sel) => sel && sel.id === contactId ? { ...sel, temperature: nextTemperature } : sel);
    try {
      await api.patch(`/contacts/${contactId}/temperature`, { temperature: nextTemperature });
    } catch (e) {
      // Rollback
      setData((d) => ({ ...d, items: d.items.map((c) => c.id === contactId ? { ...c, temperature: prev } : c) }));
      setSelected((sel) => sel && sel.id === contactId ? { ...sel, temperature: prev } : sel);
      setError("Could not update lead temperature.");
    }
  };

  const replyByEmail = (contact) => {
    const to = contact.email || contact.email_raw;
    if (!to) {
      setError("This contact has no email address on file.");
      return;
    }
    // Open a plain mailto: link — pops the user's default mail client
    // (Apple Mail / Outlook) with To pre-filled. Subject prefixed with
    // "Re:" if we have one. No HTML body / signature — the local mail
    // client uses its own signature.
    const subject = contact.subject ? `Re: ${contact.subject}` : "";
    const params = subject ? `?subject=${encodeURIComponent(subject)}` : "";
    window.location.href = `mailto:${encodeURIComponent(to)}${params}`;
    // Auto-advance to "Contacted" if currently "new" — same UX as before.
    if (!contact.pipeline_status || contact.pipeline_status === "new") {
      updateStage(contact.id, "contacted");
    }
  };

  const promote = async (contactId) => {
    try { await api.patch(`/contacts/${contactId}/promote`); setSelected(null); load(); }
    catch (e) { setError("Could not promote contact."); }
  };

  const demote = async (contactId, target) => {
    if (!window.confirm(`Remove from sales pipeline and return to ${target === "franchise" ? "Franchise Contacts" : "General Contacts"}?`)) return;
    try { await api.patch(`/contacts/${contactId}/demote`); setSelected(null); load(); }
    catch (e) { setError("Could not demote contact."); }
  };

  const remove = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      setSelected(null);
      setData((d) => ({ ...d, items: d.items.filter((c) => c.id !== contactId) }));
    } catch (e) { setError("Could not delete contact."); }
  };

  // Phase 1.7 — One-click Convert to Franchisee/Licencee
  const convertContact = async (contact, viewOnly) => {
    // If already converted, just navigate
    if (viewOnly || contact.converted_to_franchisee_id) {
      const fid = contact.converted_to_franchisee_id;
      if (fid) navigate(`/franchisees/${fid}`);
      return;
    }
    try {
      const { data: res } = await api.post(`/contacts/${contact.id}/convert-to-franchisee`);
      const fid = res?.franchisee?.id;
      // Update the contact in-place to reflect "converted" status — removes from pipeline
      setData((d) => ({
        ...d,
        items: d.items.map((c) => c.id === contact.id
          ? { ...c, in_pipeline: false, pipeline_status: null,
              converted_to_franchisee_id: fid, converted_to_record_type: res.record_type }
          : c),
      }));
      setSelected(null);
      if (!fid) return;
      const inv = res?.launch_portal_invitation || {};
      if (inv.skipped_reason === "no_discovery_portal") {
        toast.error(
          "Launch Portal welcome email NOT sent: this contact has no active Discovery Portal link. Provide the Discovery Portal link, then send the welcome manually.",
          { duration: 15000 },
        );
      }
      // Surface territory link status — auto-linked plan or missing/needs review
      if (res?.territory_linked) {
        const homes = res?.territory_home_count;
        const homesNote = (homes !== null && homes !== undefined)
          ? ` (${homes.toLocaleString()} homes)`
          : "";
        toast.success(
          `Territory linked from prospect plan — ${res.territory_sectors?.length || 0} sectors${homesNote}.`,
        );
        navigate(`/franchisees/${fid}`);
      } else {
        // No prebuilt plan → ask the admin whether to open the builder now
        const goBuild = window.confirm(
          "Franchisee created, but no territory was defined for this contact yet.\n\n"
          + "Open the Territory Builder now to draw one?",
        );
        navigate(goBuild ? `/territory-builder?franchisee_id=${fid}` : `/franchisees/${fid}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not convert contact.";
      setError(typeof msg === "string" ? msg : "Could not convert contact.");
    }
  };

  // Link contact to an EXISTING franchisee (no new record created).
  // The actual API call happens inside the modal; we just handle the
  // post-link side-effects here (in-place row update + navigation).
  const [linkingContact, setLinkingContact] = useState(null);
  const openLinkExisting = (contact) => setLinkingContact(contact);

  // Merge state — `mergePair` holds the two contacts to merge. Triggered
  // either from the bulk-bar (exactly 2 selected), the drawer, or the
  // Duplicate Finder modal.
  const [mergePair, setMergePair] = useState(null);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [duplicatesReloadAt, setDuplicatesReloadAt] = useState(0);
  const openMergeFromBulkBar = () => {
    if (selectedIds.size !== 2) return;
    const ids = [...selectedIds];
    const a = data.items.find((c) => c.id === ids[0]);
    const b = data.items.find((c) => c.id === ids[1]);
    if (a && b) setMergePair({ a, b });
  };
  const handleMerged = (result) => {
    // Remove the loser from the local list and replace the survivor with
    // the freshly-returned doc so the kanban + drawer reflect the merge
    // without needing a full reload.
    setData((d) => ({
      ...d,
      items: d.items
        .filter((c) => c.id !== result.loser_id)
        .map((c) => c.id === result.survivor_id ? { ...c, ...result.survivor } : c),
    }));
    setSelectedIds(new Set());
    setMergePair(null);
    setSelected(result.survivor || null);
    // If the merge was launched from the Duplicate Finder, refresh the
    // group list so the loser drops off and groups of 2 disappear.
    if (duplicatesOpen) setDuplicatesReloadAt(Date.now());
  };
  const handleLinked = (franchiseeId) => {
    if (!linkingContact) return;
    setData((d) => ({
      ...d,
      items: d.items.map((c) => c.id === linkingContact.id
        ? { ...c, in_pipeline: false, pipeline_status: null,
            converted_to_franchisee_id: franchiseeId,
            linked_to_existing: true }
        : c),
    }));
    setLinkingContact(null);
    setSelected(null);
    navigate(`/franchisees/${franchiseeId}`);
  };

  const grouped = useMemo(() => {
    const g = STAGES.reduce((acc, s) => ({ ...acc, [s.key]: [] }), {});
    visibleItems.forEach((c) => {
      const stage = c.pipeline_status && g[c.pipeline_status] ? c.pipeline_status : "new";
      g[stage].push(c);
    });
    // When the "Sort by Hot Lead" toggle is on, every column sorts its
    // own cards by AUTO score desc (hottest first). Contacts with no
    // engagement signal fall to the bottom. Stable for ties.
    if (sortByHot) {
      Object.keys(g).forEach((k) => {
        g[k] = [...g[k]].sort((a, b) => (tempMap[b.id]?.score || 0) - (tempMap[a.id]?.score || 0));
      });
    }
    return g;
  }, [visibleItems, sortByHot, tempMap]);

  const stats = useMemo(() => {
    const s = { total: visibleItems.length };
    STAGES.forEach((stg) => { s[stg.key] = (grouped[stg.key] || []).length; });
    return s;
  }, [visibleItems, grouped]);

  const currentTab = TABS.find((t) => t.key === tab);
  const isPipeline = tab === "pipeline";

  return (
    <div className="min-h-screen">
      <BackfillHealthBanner />
      <div className="h-16 border-b border-stone-200 bg-white flex items-center px-8 sticky top-0 z-10" data-testid="topbar">
        <div className="flex items-baseline gap-3 flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">CRM</div>
          <h1 className="font-display text-xl text-stone-950">{currentTab?.label}</h1>
          <span className="text-xs text-stone-500">{data.total.toLocaleString()} records</span>
        </div>
        <div className="flex items-center gap-3">
          {isPipeline && (() => {
            // Count contacts currently in the ``follow_up_due`` stage
            // — this is the "chase list" HQ needs to see the moment
            // they open the CRM. Chip is always rendered so a count of
            // 0 is a positive "inbox zero" signal; click switches to
            // pipeline view, scrolls the column into view and briefly
            // highlights it.
            const dueCount = data.items.filter(
              (c) => c.pipeline_status === "follow_up_due"
            ).length;
            return (
              <button
                type="button"
                onClick={() => {
                  setView("pipeline");
                  setFocusStage("follow_up_due");
                }}
                title={dueCount ? `${dueCount} contact${dueCount === 1 ? "" : "s"} waiting for a follow-up email` : "No contacts waiting for a follow-up"}
                data-testid="followup-due-chip"
                className={`px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-lg border inline-flex items-center gap-1.5 transition-colors ${
                  dueCount > 0
                    ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
                    : "bg-white hover:bg-stone-50 text-stone-500 border-stone-300"
                }`}>
                <Mail className="w-3 h-3" />
                Follow-up Due
                <span className={`ml-1 tabular-nums ${dueCount > 0 ? "text-amber-100" : "text-stone-400"}`}>·</span>
                <span className="tabular-nums" data-testid="followup-due-count">
                  {dueCount} {dueCount === 1 ? "waiting" : "waiting"}
                </span>
              </button>
            );
          })()}
          {isPipeline && view === "pipeline" && (
            <button
              type="button"
              onClick={() => setSortByHot((v) => !v)}
              data-testid="sort-by-hot-toggle"
              title="Sort columns by AUTO Lead Temperature score (hottest first)"
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-lg border inline-flex items-center gap-1.5 transition-colors ${
                sortByHot
                  ? "bg-orange-600 hover:bg-orange-700 text-white border-orange-600"
                  : "bg-white hover:bg-stone-50 text-stone-700 border-stone-300"
              }`}>
              <Flame className="w-3 h-3" /> Sort by Hot
            </button>
          )}
          {isPipeline && (
            <div className="flex border border-stone-300 rounded-lg overflow-hidden">
              <button onClick={() => setView("list")} data-testid="view-list" className={`px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${view === "list" ? "bg-stone-950 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}>
                <Rows3 className="w-3 h-3" /> Tabs
              </button>
              <button onClick={() => setView("pipeline")} data-testid="view-pipeline" className={`px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${view === "pipeline" ? "bg-stone-950 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}>
                <Kanban className="w-3 h-3" /> Pipeline
              </button>
            </div>
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contact-search"
              placeholder="Search…"
              autoComplete="off"
              onFocus={() => setSearchFocused(true)}
              onBlur={() => { setTimeout(() => setSearchFocused(false), 150); }}
              className={`pl-9 ${search ? "pr-9" : "pr-3"} py-2 w-56 bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:border-stone-900 rounded-lg`} />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                data-testid="contact-search-clear"
                aria-label="Clear search"
                title="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-900 hover:bg-stone-200 rounded-md transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Recent Searches dropdown — shows when the input is focused
                and there's at least one historical query AND the user
                hasn't started typing a new one (so it never gets in the
                way of an active search). Last 10 queries, most-recent
                first; click to re-run, or "Clear" to wipe the list. */}
            {searchFocused && !search && recentSearches.length > 0 && (
              <div
                onMouseDown={(e) => e.preventDefault()}
                className="absolute right-0 top-full mt-1 z-30 w-72 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden"
                data-testid="recent-searches-dropdown">
                <div className="px-3 py-2 flex items-center justify-between bg-stone-50 border-b border-stone-200">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> Recent searches
                  </div>
                  <button
                    type="button"
                    onClick={() => { clearRecentSearches(); setRecentSearches([]); }}
                    data-testid="recent-searches-clear"
                    className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 hover:text-stone-900">
                    Clear
                  </button>
                </div>
                <ul className="max-h-72 overflow-y-auto">
                  {recentSearches.map((q) => (
                    <li key={q}>
                      <button
                        type="button"
                        onClick={() => { setSearch(q); setSearchFocused(false); }}
                        data-testid={`recent-search-${q.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                        className="w-full text-left px-3 py-2 text-sm text-stone-800 hover:bg-stone-50 flex items-center gap-2">
                        <Search className="w-3 h-3 text-stone-400" />
                        <span className="truncate">{q}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <button onClick={() => setDuplicatesOpen(true)} data-testid="find-duplicates-button"
            className="px-3 py-2 border border-stone-300 bg-white text-stone-900 text-xs font-bold uppercase tracking-wider hover:bg-stone-50 transition-colors rounded-lg flex items-center gap-1.5">
            <GitMerge className="w-3.5 h-3.5" /> Find Duplicates
          </button>
          <button onClick={() => setImportOpen(true)} data-testid="import-csv-button"
            className="px-3 py-2 border border-stone-300 bg-white text-stone-900 text-xs font-bold uppercase tracking-wider hover:bg-stone-50 transition-colors rounded-lg flex items-center gap-1.5">
            <Upload className="w-3.5 h-3.5" /> Import CSV
          </button>
          <button onClick={exportContactsCsv} data-testid="export-csv-button"
            title={`Export the currently-visible ${tab || "contacts"} list as CSV`}
            disabled={exportBusy}
            className="px-3 py-2 border border-stone-300 bg-white text-stone-900 text-xs font-bold uppercase tracking-wider hover:bg-stone-50 disabled:opacity-60 disabled:cursor-progress transition-colors rounded-lg flex items-center gap-1.5">
            {exportBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            {exportBusy ? "Exporting…" : "Export CSV"}
          </button>
          <button onClick={() => setAddOpen(true)} data-testid="add-contact-button"
            className="px-4 py-2 bg-stone-950 text-white text-xs font-bold uppercase tracking-wider hover:bg-stone-800 transition-colors rounded-lg flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add Contact
          </button>
        </div>
      </div>

      <div className="px-8 pt-4 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setHeaderExpanded((v) => !v)}
          data-testid="header-expand-toggle"
          title={headerExpanded ? "Hide tab picker and filters" : "Show tab picker and filters"}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-lg border border-stone-300 bg-white text-stone-800 hover:bg-stone-50 transition-colors"
        >
          {headerExpanded ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
          {headerExpanded ? "Hide Full View" : "Expand Full View"}
        </button>
        {/* When collapsed we still surface which tab you're on so the
            page never loses its context — otherwise the top bar's
            small title is the only breadcrumb. */}
        {!headerExpanded && currentTab && (
          <span className="text-[11px] text-stone-500 flex items-center gap-1.5" data-testid="header-collapsed-tab-badge">
            <currentTab.icon className="w-3.5 h-3.5" />
            <span className="font-bold text-stone-800 uppercase tracking-wider">{currentTab.label}</span>
            {typeof counts[currentTab.key] === "number" && (
              <span className="tabular-nums text-stone-500">· {counts[currentTab.key].toLocaleString()}</span>
            )}
          </span>
        )}
      </div>

      {headerExpanded && (
      <div className="px-8 pt-4">
        <div className="flex flex-wrap gap-1 -mb-px" data-testid="mode-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.icon;
            const count = counts[t.key];
            const newCount = counts[`new_${t.key}`] || 0;
            return (
              <button key={t.key} onClick={() => { setTab(t.key); setStageFilter(""); }} data-testid={`mode-${t.key}`}
                className={`px-5 py-3 text-sm font-bold transition-colors rounded-t-xl flex items-start gap-2 ${
                  active ? "bg-white text-stone-950 border border-stone-200 border-b-white" : "text-stone-500 hover:text-stone-900 hover:bg-stone-100/50"
                }`}>
                <Icon className="w-4 h-4 mt-0.5" />
                <span className="text-left">
                  <span className="inline-flex items-center gap-1.5">
                    {t.label}
                    {typeof count === "number" && (
                      <span
                        data-testid={`tab-count-${t.key}`}
                        className={`tabular-nums text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
                          active
                            ? "bg-stone-950 text-white border-stone-950"
                            : "bg-stone-100 text-stone-700 border-stone-200"
                        }`}
                      >
                        {count.toLocaleString()}
                      </span>
                    )}
                    {/* Red "new inbound" badge — mirrors the sidebar Sales
                        & Contacts alert style so the visual language is
                        consistent. Only rendered when there's something
                        new to draw the eye to. */}
                    {newCount > 0 && (
                      <span
                        data-testid={`tab-new-${t.key}`}
                        title={newCount === 1 ? "1 new" : `${newCount} new`}
                        className="shrink-0 min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold tabular-nums shadow-sm"
                      >
                        {newCount > 99 ? "99+" : newCount}
                      </span>
                    )}
                  </span>
                  <div className={`text-[10px] font-normal mt-0.5 ${active ? "text-stone-600" : "text-stone-400"}`}>{t.hint}</div>
                </span>
              </button>
            );
          })}
        </div>
        <div className="border-b border-stone-200" />
      </div>
      )}

      {search && search.trim() && (
        <div className="px-8 pt-3" data-testid="cross-tab-search-banner">
          <div className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-2 flex items-center gap-3 text-xs text-stone-700">
            <Search className="w-3.5 h-3.5 text-stone-500 shrink-0" />
            <span>
              Searching across <strong>all tabs</strong> for &ldquo;{search.trim()}&rdquo; — results show their source pill so you can tell which tab each contact lives in.
            </span>
            <button
              type="button"
              onClick={() => setSearch("")}
              data-testid="cross-tab-search-clear"
              className="ml-auto px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-white border border-stone-300 hover:bg-stone-100 rounded-md transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Bulk action bar — appears when one or more contacts selected */}
      {selectedIds.size > 0 && (
        <div className="px-8 pt-4">
          <div className="bg-stone-950 text-white rounded-2xl px-5 py-3 flex items-center gap-4 shadow-lg" data-testid="bulk-action-bar">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-[#dddd16]" />
              <span className="text-sm font-bold tabular-nums">{selectedIds.size} selected</span>
            </div>
            <div className="flex-1" />
            {/* Bulk Reply — opens the shared ReplyWithTemplateModal in
                bulk mode. Enabled when 1..MAX_BULK_BATCH contacts are
                selected; disabled with an explanatory tooltip beyond
                that. Sam's Feb 2026 brief: reuses the existing send
                pipeline per-recipient so template auto-advance,
                correspondence logging, and follow-up-count bumps fire
                identically to a single-contact send. */}
            {(() => {
              const cap = 10;
              const disabled = selectedIds.size === 0 || selectedIds.size > cap;
              return (
                <button
                  type="button"
                  onClick={() => {
                    if (disabled) return;
                    // Snapshot the currently-selected contact docs so
                    // the modal keeps its recipient list even if the
                    // admin toggles the underlying selection.
                    const picked = data.items.filter((c) => selectedIds.has(c.id));
                    setBulkReplyContacts(picked);
                  }}
                  disabled={disabled}
                  data-testid="bulk-reply"
                  title={
                    selectedIds.size === 0
                      ? "Select 1–10 contacts to enable Bulk Reply"
                      : selectedIds.size > cap
                        ? `Bulk Reply is capped at ${cap} contacts per send — deselect ${selectedIds.size - cap} to proceed`
                        : `Open Bulk Reply with ${selectedIds.size} selected contact${selectedIds.size === 1 ? "" : "s"}`
                  }
                  className="touch-target px-3 text-xs font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 hover:bg-[#aaaa11] rounded-lg flex items-center gap-1.5 disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Bulk Reply {selectedIds.size > 0 && `(${Math.min(selectedIds.size, cap)}${selectedIds.size > cap ? "/10" : ""})`}
                </button>
              );
            })()}
            <MoveMenu onMove={bulkMove} onDelete={bulkDelete} label="Move selected" testid="bulk-move" currentTab={tab} count={selectedIds.size} />
            {/* Standalone Delete Selected — mirrors the destructive
                option inside the Move dropdown so admins can nuke a
                selection in one click without hunting for it under a
                menu.  Uses the SAME ``bulkDelete`` handler (native
                window.confirm + tombstoning of the Gravity Forms
                entries) so there's only ever one code path for the
                delete. */}
            <button
              type="button"
              onClick={bulkDelete}
              data-testid="bulk-delete"
              title={`Permanently delete ${selectedIds.size} selected contact${selectedIds.size === 1 ? "" : "s"}`}
              className="touch-target px-3 text-xs font-bold uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete selected ({selectedIds.size})
            </button>
            {selectedIds.size === 2 && (
              <button onClick={openMergeFromBulkBar} data-testid="bulk-merge"
                className="touch-target px-3 text-xs font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 hover:bg-[#aaaa11] rounded-lg flex items-center gap-1.5">
                <GitMerge className="w-3.5 h-3.5" /> Merge these 2
              </button>
            )}
            <button onClick={clearSelection} data-testid="bulk-clear"
              className="px-3 py-1 text-xs font-bold uppercase tracking-wider bg-white/10 hover:bg-white/20 text-white rounded-lg flex items-center gap-1">
              <X className="w-3 h-3" /> Clear
            </button>
          </div>
        </div>
      )}

      {/* Non-pipeline tabs (Franchise / Licence / Care Home / Art Kit /
          General) — expose ONLY the date range filter at the top. AGE
          tier chips + TYPE + FOLLOW-UP are pipeline-only. Feb 2026:
          Sam wanted the same From / To date narrowing to be available
          across every list-view so records can be pulled for a
          specific window without hunting through pages. */}
      {!isPipeline && !loading && data.items.length > 0 && (
        <div className="px-8 pt-6">
          <div
            className="flex items-center gap-2 flex-wrap"
            data-testid="date-range-filter"
          >
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mr-1">Date range</span>
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] uppercase tracking-wider font-bold text-stone-500" htmlFor="date-range-from-nonpipeline">From</label>
              <input
                id="date-range-from-nonpipeline"
                type="date"
                value={dateRangeFilter.from}
                onChange={(e) => setDateRangeFilter((r) => ({ ...r, from: e.target.value }))}
                max={dateRangeFilter.to || undefined}
                data-testid="date-range-from"
                className="px-2 py-1 text-[11px] font-bold text-stone-800 bg-white border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] uppercase tracking-wider font-bold text-stone-500" htmlFor="date-range-to-nonpipeline">To</label>
              <input
                id="date-range-to-nonpipeline"
                type="date"
                value={dateRangeFilter.to}
                onChange={(e) => setDateRangeFilter((r) => ({ ...r, to: e.target.value }))}
                min={dateRangeFilter.from || undefined}
                data-testid="date-range-to"
                className="px-2 py-1 text-[11px] font-bold text-stone-800 bg-white border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
              />
            </div>
            {(dateRangeFilter.from || dateRangeFilter.to) && (
              <>
                <button
                  type="button"
                  onClick={() => setDateRangeFilter({ from: "", to: "" })}
                  data-testid="date-range-clear"
                  title="Clear date range"
                  className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider border border-stone-300 rounded-lg text-stone-700 bg-white hover:bg-stone-50 transition-colors"
                >
                  Clear
                </button>
                <span className="text-[11px] text-stone-500 tabular-nums" data-testid="date-range-count">
                  {visibleItems.length} of {data.items.length}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {isPipeline && headerExpanded && !loading && data.items.length > 0 && (
        <div className="px-8 pt-6 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap" data-testid="age-filter">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mr-1">Age</span>
              {AGE_TIERS.map((t) => {
                const active = ageFilter === t.key;
                const count = t.key === "all"
                  ? data.items.length
                  : data.items.filter((c) => t.test(daysSince(c.date || c.date_added))).length;
                return (
                  <button key={t.key} onClick={() => setAgeFilter(t.key)} data-testid={`age-tier-${t.key}`}
                    className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider border rounded-lg transition-colors ${
                      active ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                    }`}>
                    {t.label} <span className={`ml-1 tabular-nums ${active ? "text-stone-300" : "text-stone-500"}`}>{count}</span>
                  </button>
                );
              })}
            </div>
            <span className="text-stone-300">·</span>
            {/* Date-range filter — Sam's Feb 2026 brief. Narrows the
                visible list to contacts whose ``date`` / ``date_added``
                falls inside the picked inclusive range. Works across
                every stage (NEW / CONTACTED / FOLLOW-UP DUE /
                INTERESTED / DORMANT / LOST) and both TABS + PIPELINE
                views because ``visibleItems`` feeds both. Native
                ``<input type="date">`` gives us a proper calendar
                popup on every desktop browser without pulling in a
                date-picker library. */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="date-range-filter">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mr-1">Date range</span>
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] uppercase tracking-wider font-bold text-stone-500" htmlFor="date-range-from">From</label>
                <input
                  id="date-range-from"
                  type="date"
                  value={dateRangeFilter.from}
                  onChange={(e) => setDateRangeFilter((r) => ({ ...r, from: e.target.value }))}
                  max={dateRangeFilter.to || undefined}
                  data-testid="date-range-from"
                  className="px-2 py-1 text-[11px] font-bold text-stone-800 bg-white border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] uppercase tracking-wider font-bold text-stone-500" htmlFor="date-range-to">To</label>
                <input
                  id="date-range-to"
                  type="date"
                  value={dateRangeFilter.to}
                  onChange={(e) => setDateRangeFilter((r) => ({ ...r, to: e.target.value }))}
                  min={dateRangeFilter.from || undefined}
                  data-testid="date-range-to"
                  className="px-2 py-1 text-[11px] font-bold text-stone-800 bg-white border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
                />
              </div>
              {(dateRangeFilter.from || dateRangeFilter.to) && (
                <button
                  type="button"
                  onClick={() => setDateRangeFilter({ from: "", to: "" })}
                  data-testid="date-range-clear"
                  title="Clear date range"
                  className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider border border-stone-300 rounded-lg text-stone-700 bg-white hover:bg-stone-50 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
            <span className="text-stone-300">·</span>
            <div className="flex items-center gap-2 flex-wrap" data-testid="source-filter">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mr-1">Type</span>
              {[
                { key: "all",       label: "All",       },
                { key: "franchise", label: "Franchise"  },
                { key: "licence",   label: "Licence"    },
              ].map((s) => {
                const active = sourceFilter === s.key;
                const count = s.key === "all"
                  ? data.items.length
                  : data.items.filter((c) => c.source === (s.key === "franchise" ? "franchise_enquiry" : "licence_enquiry")).length;
                const dotColor = s.key === "franchise" ? "bg-stone-500" : s.key === "licence" ? "bg-indigo-500" : "bg-stone-300";
                return (
                  <button key={s.key} onClick={() => setSourceFilter(s.key)} data-testid={`source-${s.key}`}
                    className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider border rounded-lg transition-colors flex items-center gap-1.5 ${
                      active ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                    }`}>
                    {s.key !== "all" && <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />}
                    {s.label} <span className={`ml-1 tabular-nums ${active ? "text-stone-300" : "text-stone-500"}`}>{count}</span>
                  </button>
                );
              })}
            </div>
            <span className="text-stone-300">·</span>
            <div className="flex items-center gap-2 flex-wrap" data-testid="followup-filter">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mr-1">Follow-up</span>
              {[
                { key: "all",      label: "All"    },
                { key: "not_sent", label: "Not sent" },
                { key: "sent",     label: "Sent"     },
              ].map((s) => {
                const active = followUpFilter === s.key;
                const count = s.key === "all"
                  ? data.items.length
                  : s.key === "sent"
                    ? data.items.filter((c) => Number(c.follow_up_sent_count || 0) >= 1).length
                    : data.items.filter((c) => Number(c.follow_up_sent_count || 0) === 0).length;
                return (
                  <button key={s.key} onClick={() => setFollowUpFilter(s.key)} data-testid={`followup-${s.key}`}
                    className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider border rounded-lg transition-colors flex items-center gap-1.5 ${
                      active ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                    }`}>
                    {s.key === "sent" && <Mail className="w-2.5 h-2.5" />}
                    {s.label} <span className={`ml-1 tabular-nums ${active ? "text-stone-300" : "text-stone-500"}`}>{count}</span>
                  </button>
                );
              })}
            </div>
            <span className="text-stone-300">·</span>
            <button
              onClick={async () => {
                if (!window.confirm("Find and remove duplicate Sales Pipeline contacts?\n\nThe same email submitted multiple times via Gravity Forms creates duplicate cards. This removes the newer 'new' duplicates when an older, already-triaged copy exists. Safe — never deletes triaged records.")) return;
                try {
                  const { data } = await api.post("/contacts/dedupe-pipeline");
                  const samples = (data.samples || []).slice(0, 5).map((s) => `${s.name || s.email}`).join("\n  • ");
                  window.alert(`Removed ${data.removed || 0} duplicate ${data.removed === 1 ? "record" : "records"}.${samples ? "\n\nIncluding:\n  • " + samples : ""}`);
                  load();
                  loadCounts();
                } catch (e) {
                  window.alert(e?.response?.data?.detail || "Dedupe failed.");
                }
              }}
              data-testid="pipeline-dedupe-btn"
              title="Find email duplicates in the pipeline and remove the 'new' copies when an older triaged version already exists"
              className="px-3 py-1 text-[11px] font-bold uppercase tracking-wider border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 rounded-lg flex items-center gap-1.5"
            >
              <RefreshCw className="w-3 h-3" /> Dedupe pipeline
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-7 gap-3" data-testid="pipeline-summary">
            <button onClick={() => setStageFilter("")} className={`bg-white border border-stone-200 rounded-2xl p-4 text-left hover:border-stone-400 transition-colors ${stageFilter === "" ? "ring-2 ring-stone-950" : ""}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Total</div>
              <div className="font-display text-2xl text-stone-950">{stats.total.toLocaleString()}</div>
            </button>
            {STAGES.map((s) => (
              <button key={s.key} onClick={() => setStageFilter(s.key === stageFilter ? "" : s.key)} data-testid={`stat-${s.key}`}
                className={`bg-white border border-stone-200 rounded-2xl p-4 text-left hover:border-stone-400 transition-colors ${stageFilter === s.key ? "ring-2 ring-stone-950" : ""}`}>
                <div className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${s.barColor}`} />
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">{s.label}</div>
                </div>
                <div className="font-display text-2xl text-stone-950">{(stats[s.key] || 0).toLocaleString()}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="p-8 pt-6">
        {error && <div className="mb-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-center gap-2 rounded-xl"><AlertCircle className="w-4 h-4" />{error}</div>}
        {loading ? (
          <div className="text-center text-stone-500 text-sm uppercase tracking-widest p-12 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : isPipeline && view === "pipeline" ? (
          <div className="flex gap-3 items-stretch" data-testid="pipeline-board">
            {STAGES.map((stage) => {
              const items = grouped[stage.key] || [];
              const collapsed = collapsedStages.has(stage.key);
              if (collapsed) {
                // Narrow vertical strip — rotated label + count, click anywhere to re-open.
                return (
                  <button
                    key={stage.key}
                    type="button"
                    onClick={() => toggleStageCollapsed(stage.key)}
                    data-testid={`pipeline-column-${stage.key}`}
                    title={`Expand ${stage.label}`}
                    className={`w-10 shrink-0 bg-white border border-stone-200 rounded-2xl overflow-hidden hover:border-stone-500 transition-colors`}>
                    <div className={`px-1 py-2.5 border-b border-stone-200 flex items-center justify-center ${stage.color.split(" ")[0]}`}>
                      <ChevronsRight className="w-3.5 h-3.5 text-stone-700" />
                    </div>
                    <div className="py-4 flex flex-col items-center gap-3" style={{ minHeight: "12rem" }}>
                      <span className="text-xs font-bold text-stone-700 tabular-nums">{items.length}</span>
                      <span
                        className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-700"
                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                        {stage.label}
                      </span>
                    </div>
                  </button>
                );
              }
              return (
                <div
                  key={stage.key}
                  className={`flex-1 min-w-0 bg-white border rounded-2xl overflow-hidden transition-all ${
                    focusStage === stage.key
                      ? "border-amber-500 ring-2 ring-amber-400 shadow-lg"
                      : "border-stone-200"
                  }`}
                  data-testid={`pipeline-column-${stage.key}`}>
                  <div className={`px-3 py-2.5 border-b border-stone-200 ${stage.color.split(" ")[0]}`}>
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => toggleStageCollapsed(stage.key)}
                        data-testid={`pipeline-column-collapse-${stage.key}`}
                        title={`Collapse ${stage.label}`}
                        className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-white/60 text-stone-700">
                        <ChevronsLeft className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-900 truncate">{stage.label}</span>
                      <span className="text-xs text-stone-700 font-bold tabular-nums">{items.length}</span>
                    </div>
                  </div>
                  <div className="p-2 space-y-1.5 max-h-[calc(100vh-22rem)] overflow-y-auto">
                    {items.slice(0, 100).map((c) => {
                      const age = daysSince(c.date || c.date_added);
                      const checked = selectedIds.has(c.id);
                      const srcStyle = SOURCE_STYLE[c.source] || SOURCE_STYLE.general_enquiry;
                      return (
                        <div key={c.id} onClick={() => openContact(c)}
                          className={`bg-white border border-stone-200 rounded-xl p-2.5 hover:border-stone-500 cursor-pointer text-xs ${srcStyle.border} ${checked ? "ring-2 ring-stone-950" : ""}`}
                          data-testid={`pipeline-card-${c.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <button onClick={(e) => { e.stopPropagation(); toggleSelect(c.id, e); }} data-testid={`card-select-${c.id}`} className="shrink-0 text-stone-400 hover:text-stone-950">
                              {checked ? <CheckSquare className="w-3.5 h-3.5 text-stone-950" /> : <Square className="w-3.5 h-3.5" />}
                            </button>
                            <div className="font-semibold text-stone-950 truncate flex-1 flex items-center gap-1">
                              <span className="truncate">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "Unnamed"}</span>
                              <ManualBadge addedBy={c.manually_added_by} />
                            </div>
                            {/* Auto-score chip — pulled from bulk
                                /api/pipeline/temperatures. Hidden if
                                the contact has no engagement (would
                                always be Cold/0 and clutter the card). */}
                            {(() => {
                              const t = tempMap[c.id];
                              if (!t || t.score === 0) return null;
                              const bandStyle = t.band === "hot"
                                ? "bg-orange-50 border-orange-300 text-orange-700"
                                : t.band === "warm"
                                ? "bg-amber-50 border-amber-300 text-amber-700"
                                : "bg-sky-50 border-sky-300 text-sky-700";
                              return (
                                <span
                                  title={`Auto-score: ${t.score} (${t.band})`}
                                  data-testid={`card-autoscore-${c.id}`}
                                  className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded border text-[9px] font-bold uppercase ${bandStyle}`}>
                                  <Flame className="w-2.5 h-2.5" />{t.score}
                                </span>
                              );
                            })()}
                            {/* Follow-up email indicator. Renders a
                                compact ✉N chip when at least one
                                follow-up email has been sent so HQ can
                                see at a glance that this contact has
                                already received a chase. Hidden when
                                follow_up_sent_count is 0. */}
                            {Number(c.follow_up_sent_count || 0) >= 1 && (
                              <span
                                title={`Follow-up email sent (${c.follow_up_sent_count}× so far)`}
                                data-testid={`card-followup-${c.id}`}
                                className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded border text-[9px] font-bold bg-emerald-50 border-emerald-300 text-emerald-700">
                                <Mail className="w-2.5 h-2.5" />{c.follow_up_sent_count}
                              </span>
                            )}
                            {/* Lead-temperature picker. Pipeline-only: any
                                contact in the kanban is by definition in
                                the pipeline, so always render here. */}
                            <TemperaturePicker
                              value={c.temperature}
                              onChange={(next) => setTemperature(c.id, next)}
                              testidPrefix={`card-temp-${c.id}`}
                            />
                          </div>
                          {c.establishment_name && <div className="text-stone-600 truncate mt-0.5 pl-5">{c.establishment_name}</div>}
                          <div className="flex items-center justify-between mt-1.5 text-[10px] pl-5 gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider border ${srcStyle.pill}`}>{srcStyle.label}</span>
                              {c.postcode && (
                                <span className="text-stone-500 truncate hidden xl:inline" title={c.postcode}>{c.postcode}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {(!c.pipeline_status || c.pipeline_status === "new") && (
                                <>
                                  {c.email && (
                                    <button onClick={(e) => { e.stopPropagation(); replyByEmail(c); }}
                                      data-testid={`reply-${c.id}`}
                                      title={`Send reply to ${c.email} (auto-advances to Contacted)`}
                                      className="px-2 py-0.5 rounded-md bg-[#E2462A] hover:bg-[#C73B22] text-white text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-sm">
                                      <Send className="w-2.5 h-2.5" /> Reply
                                    </button>
                                  )}
                                  <button onClick={(e) => { e.stopPropagation(); updateStage(c.id, "contacted"); }}
                                    data-testid={`mark-contacted-${c.id}`}
                                    title="Mark Contacted — use this if you've replied in your own email app"
                                    className="px-1.5 py-0.5 rounded-md bg-white border border-stone-300 text-stone-700 hover:bg-stone-950 hover:text-white hover:border-stone-950 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 transition-colors">
                                    <CheckCircle2 className="w-2.5 h-2.5" />
                                  </button>
                                </>
                              )}
                              <AgeBadge days={age} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {items.length === 0 && <div className="text-[10px] text-stone-400 px-1 py-2 text-center">No enquiries</div>}
                    {items.length > 100 && <div className="text-[10px] text-stone-500 px-1">+{items.length - 100} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (() => {
          // Sales-Pipeline TABS view — shown when the user picks the
          // "Tabs" toggle. Renders four full-width panels (NEW /
          // CONTACTED / FOLLOW-UP DUE / INTERESTED) with expandable
          // rows for at-a-glance actioning. Dormant + Lost remain on
          // the kanban only. See SalesPipelineTabsView.jsx for the
          // full design rationale.
          if (isPipeline && view === "list") {
            return (
              <SalesPipelineTabsView
                contacts={listItems}
                tempMap={tempMap}
                initialExpandedId={initialExpandedId}
                onInitialExpandedConsumed={() => setInitialExpandedId(null)}
                onOpenContact={(c) => openContact(c)}
                onReplyContact={(c) => setKanbanReplyContact(c)}
                onOpenPostcodeMap={(c) => setPostcodeMapContact(c)}
                onStageChange={updateStage}
                onDemote={demote}
                onConvert={convertContact}
                onLinkExisting={openLinkExisting}
                onMarkFollowUpSent={markFollowUpSent}
                onChangeSource={changeContactSource}
                onTemperatureChange={setTemperature}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onContactUpdated={(id, patch) => {
                  // Merge the patch into local state so the row's chip /
                  // notes / checklist stay in sync without a full reload.
                  setData((d) => ({
                    ...d,
                    items: d.items.map((x) => x.id === id ? { ...x, ...patch } : x),
                  }));
                }}
              />
            );
          }
          // Common table block reused by both layouts — full-width for
          // most tabs, wrapped inside a ResizablePanel for care_home.
          const tableView = (
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden h-full" data-testid="contacts-table">
            <table className="w-full">
              <thead className="bg-[#F2F2F0] border-b border-stone-200">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <button onClick={(e) => {
                      e.stopPropagation();
                      const visibleIds = listItems.slice(0, 500).map((c) => c.id);
                      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
                      setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
                      setLastSelectedId(null);
                    }} data-testid="select-all" className="text-stone-500 hover:text-stone-900">
                      {listItems.length > 0 && listItems.slice(0, 500).every((c) => selectedIds.has(c.id)) ?
                        <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </button>
                  </th>
                  <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">Name / Establishment</th>
                  <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 w-32">Date</th>
                  <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">Contact</th>
                  <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 w-32">Location</th>
                  {tab !== "care_home" && (
                    <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 w-28">Source</th>
                  )}
                  {isPipeline && <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 w-32">Stage</th>}
                  <th className="text-right px-3 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 w-32">Move</th>
                </tr>
              </thead>
              <tbody>
                {listItems.length === 0 ? (
                  <tr><td colSpan={(isPipeline ? 8 : 7) - (tab === "care_home" ? 1 : 0)} className="px-3 py-10 text-center text-sm text-stone-500">No records.</td></tr>
                ) : listItems.slice(0, displayLimit).map((c) => {
                  const checked = selectedIds.has(c.id);
                  const age = daysSince(c.date || c.date_added);
                  // "Unseen" red dot — the contact hasn't been reviewed
                  // yet. Two ways for a contact to count as reviewed:
                  //   1. seen_at stamped (drawer opened), OR
                  //   2. already being worked in the pipeline past the
                  //      initial "new" stage (contacted/interested/etc.)
                  // Contacts still at pipeline_status="new" keep the red
                  // dot because they legitimately haven't been triaged.
                  // Backend /counts uses the same rule so the tab badge
                  // and the row dots stay in sync.
                  const workedInPipeline = c.in_pipeline && !["new", null, "", undefined].includes(c.pipeline_status);
                  const isNew = !isPipeline && !c.seen_at && !workedInPipeline;
                  return (
                  <tr key={c.id}
                    onClick={() => openContact(c)}
                    onMouseEnter={tab === "care_home" ? () => setHoveredContactId(c.id) : undefined}
                    onMouseLeave={tab === "care_home" ? () => setHoveredContactId(null) : undefined}
                    className={`border-b border-stone-100 last:border-0 hover:bg-stone-50 cursor-pointer ${checked ? "bg-[#dddd16]/5" : ""} ${isNew ? "bg-red-50/40" : ""}`} data-testid={`contact-row-${c.id}`}>
                    <td className="px-3 py-2" onClick={(e) => { e.stopPropagation(); toggleSelect(c.id, e); }}>
                      <button data-testid={`select-${c.id}`} className="text-stone-500 hover:text-stone-900">
                        {checked ? <CheckSquare className="w-4 h-4 text-stone-950" /> : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <div className={`text-sm text-stone-950 flex items-center gap-1.5 flex-wrap ${isNew ? "font-extrabold" : "font-semibold"}`}>
                        {isNew && (
                          <span
                            data-testid={`row-new-dot-${c.id}`}
                            title="New — not yet reviewed"
                            className="inline-block w-2 h-2 rounded-full bg-red-600 shrink-0"
                          />
                        )}
                        {[c.first_name, c.last_name].filter(Boolean).join(" ") || "(no name)"}
                        <ManualBadge addedBy={c.manually_added_by} />
                        {!isPipeline && c.in_pipeline && (
                          <span data-testid={`in-pipeline-${c.id}`}
                            className="px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 border border-stone-950 flex items-center gap-1"
                            title="Also in the Sales Pipeline">
                            <Kanban className="w-2.5 h-2.5" />
                            In Pipeline{c.pipeline_status ? ` · ${c.pipeline_status.replace(/_/g, " ")}` : ""}
                          </span>
                        )}
                      </div>
                      {c.establishment_name && <div className={`text-xs ${isNew ? "text-stone-900 font-bold" : "text-stone-600"}`}>{c.establishment_name}</div>}
                    </td>
                    <td className={`px-3 py-2 text-xs ${isNew ? "text-stone-900 font-bold" : "text-stone-500"}`}>
                      <div className="flex items-center gap-1.5 tabular-nums">
                        <span>{formatDate(c.date || c.date_added)}</span>
                        {isPipeline && <AgeBadge days={age} />}
                      </div>
                    </td>
                    <td className={`px-3 py-2 text-xs ${isNew ? "text-stone-900 font-bold" : "text-stone-600"}`}>
                      <div>{c.email || c.email_raw || "—"}</div>
                      <div className={isNew ? "text-stone-700" : "text-stone-400"}>{c.telephone || c.mobile_phone || ""}</div>
                    </td>
                    <td className={`px-3 py-2 text-xs ${isNew ? "text-stone-900 font-bold" : "text-stone-700"}`}>{[c.city, c.postcode].filter(Boolean).join(" · ") || "—"}</td>
                    {tab !== "care_home" && (
                      <td className="px-3 py-2 text-xs text-stone-700"><SourcePill source={c.source} /></td>
                    )}
                    {isPipeline && <td className="px-3 py-2"><StageBadge status={c.pipeline_status} /></td>}
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <MoveMenu
                        onMove={(target, stage) => moveContact(c.id, target, stage)}
                        label="Move" testid={`row-move-${c.id}`}
                        currentTab={tab}
                        contactSource={c.source}
                        inPipeline={!!c.in_pipeline} />
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
            {listItems.length > displayLimit && (
              <div className="px-3 py-3 text-xs text-stone-500 border-t border-stone-100 flex items-center justify-between gap-3 flex-wrap">
                <span data-testid="list-pagination-status">
                  Showing first {displayLimit.toLocaleString()} of {listItems.length.toLocaleString()}.
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setDisplayLimit((n) => Math.min(listItems.length, n + 500))}
                    data-testid="list-show-more"
                    className="px-3 py-1.5 border border-stone-300 bg-white text-stone-900 text-[11px] font-bold uppercase tracking-wider hover:bg-stone-50 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    Show 500 more
                  </button>
                  <button
                    type="button"
                    onClick={() => setDisplayLimit(listItems.length)}
                    data-testid="list-show-all"
                    className="px-3 py-1.5 border border-stone-300 bg-white text-stone-900 text-[11px] font-bold uppercase tracking-wider hover:bg-stone-50 rounded-lg transition-colors"
                  >
                    Show all {listItems.length.toLocaleString()}
                  </button>
                </div>
              </div>
            )}
            {listItems.length > 500 && listItems.length <= displayLimit && (
              <div className="px-3 py-2 text-xs text-stone-500 border-t border-stone-100" data-testid="list-pagination-status">
                Showing all {listItems.length.toLocaleString()} records.
              </div>
            )}
            </div>
          );
          if (tab !== "care_home") return tableView;
          return (
            <ResizablePanelGroup
              direction="horizontal"
              autoSaveId="care-home-split"
              className="min-h-[720px]"
              data-testid="care-home-split-wrap"
            >
              <ResizablePanel defaultSize={55} minSize={30}>
                {tableView}
              </ResizablePanel>
              <ResizableHandle withHandle className="mx-2" />
              <ResizablePanel defaultSize={45} minSize={25}>
                <CareHomeMapPanel
                  filter={careHomeFilter}
                  onFilterChange={setCareHomeFilter}
                  pins={careHomePinsInRadius}
                  allPins={careHomeMap.pins}
                  loading={careHomeMap.loading}
                  error={careHomeMap.error}
                  overlay={careHomeOverlay}
                  franchisees={overlayFranchiseesFor(careHomeOverlay)}
                  radius={careHomeRadius}
                  onRadiusChange={setCareHomeRadius}
                  radiusMode={radiusMode}
                  onRadiusModeChange={setRadiusMode}
                  hoveredContactId={hoveredContactId}
                  onSelectContact={(pinId) => {
                    const c = data.items.find((x) => x.id === pinId);
                    if (c) openContact(c);
                  }}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          );
        })()}
      </div>

      <ContactDrawer contact={selected} onClose={() => setSelected(null)}
        onStageChange={updateStage} onPromote={promote}
        onMarkFollowUpSent={markFollowUpSent}
        onDemote={(id) => {
          const src = selected?.source;
          const target = src === "licence_enquiry" ? "licence" : src === "franchise_enquiry" ? "franchise" : "general";
          return moveContact(id, target);
        }}
        onDelete={remove}
        onReply={replyByEmail}
        onConvert={convertContact}
        onLinkExisting={openLinkExisting}
        onAdminNotesUpdated={(id, notes, updatedAt) => {
          setData((d) => ({
            ...d,
            items: d.items.map((c) => c.id === id
              ? { ...c, admin_notes: notes, admin_notes_updated_at: updatedAt }
              : c),
          }));
          setSelected((sel) => sel && sel.id === id
            ? { ...sel, admin_notes: notes, admin_notes_updated_at: updatedAt }
            : sel);
        }}
        onChecklistChanged={(payload) => {
          // Mirror checklist booleans + companion fields back into the cached
          // contact list so anything else listening to that data (counts,
          // badges) stays in sync without a full re-fetch.
          const patch = {
            territory_defined: payload.territory_defined,
            contract_sent: payload.contract_sent,
            shadow_day_booked: payload.shadow_day_booked,
            shadow_day_date: payload.shadow_day_date,
            shadowing_with: payload.shadowing_with,
            training_days_booked: payload.training_days_booked,
            training_day_dates: payload.training_day_dates,
            checklist_updated_at: payload.checklist_updated_at,
            launch_checklist: payload.launch_checklist,
            launch_checklist_updated_at: payload.launch_checklist_updated_at,
          };
          // Strip undefined so a partial payload (e.g. only the launch
          // checklist saved) doesn't blow away the other cached fields.
          for (const k of Object.keys(patch)) if (patch[k] === undefined) delete patch[k];
          setData((d) => ({
            ...d,
            items: d.items.map((c) => c.id === payload.id ? { ...c, ...patch } : c),
          }));
          setSelected((sel) => sel && sel.id === payload.id ? { ...sel, ...patch } : sel);
        }}
        onChangeSource={changeContactSource} />
      <LinkExistingFranchiseeModal
        open={!!linkingContact}
        contact={linkingContact}
        onClose={() => setLinkingContact(null)}
        onLinked={handleLinked} />
      <MergeContactsModal
        open={!!mergePair}
        contactA={mergePair?.a}
        contactB={mergePair?.b}
        onClose={() => setMergePair(null)}
        onMerged={handleMerged} />
      <DuplicatesModal
        open={duplicatesOpen}
        reloadAt={duplicatesReloadAt}
        onClose={() => setDuplicatesOpen(false)}
        onPickPair={(a, b) => setMergePair({ a, b })} />
      <AddContactModal open={addOpen} onClose={() => setAddOpen(false)}
        defaultTarget={tab === "pipeline" ? "franchise" : tab}
        onCreated={(_c, target) => {
          if (target && target !== tab) setTab(target);
          load();
        }} />
      <ImportCsvModal open={importOpen} onClose={() => setImportOpen(false)}
        defaultTarget={tab === "pipeline" ? "licence" : tab}
        onImported={(_r, target) => {
          if (target && target !== tab) setTab(target);
          load();
        }} />
      {/* Kanban quick-Reply modal — same flow as the drawer's "Reply with
          template" button but launched from the orange paper-plane button
          on each pipeline card. Sends via Resend, picks template by
          contact source, carries signature + attachments. */}
      <ReplyWithTemplateModal
        open={!!kanbanReplyContact}
        contact={kanbanReplyContact}
        onClose={() => setKanbanReplyContact(null)}
        onSent={() => { setKanbanReplyContact(null); load(); }}
      />
      {/* Bulk Reply modal — same component as the single-reply modal
          but fed a ``contacts`` array. Loops one API call per
          recipient so per-contact auto-advance / correspondence /
          follow-up-count logic all fire identically to a single
          send. Selection isn't cleared until a fully-successful
          batch — partial failures keep the row highlights so admins
          can retry the failed ones. */}
      <ReplyWithTemplateModal
        open={!!bulkReplyContacts}
        contacts={bulkReplyContacts || []}
        onClose={() => setBulkReplyContacts(null)}
        onSent={(_results, meta) => {
          setBulkReplyContacts(null);
          // On any successful send, refresh the list so auto-
          // advanced contacts move out of the current stage and the
          // stat cards recount. Selection is cleared after refresh
          // to avoid an orphaned selectedIds set pointing at
          // stage-changed contacts.
          if (meta?.sentCount) {
            clearSelection();
            load();
          }
        }}
      />
      {/* Postcode-map modal launched from the Sales Pipeline TABS row —
          shares the same component the drawer uses so the map UX is
          consistent everywhere. */}
      {postcodeMapContact && (
        <ContactPostcodeMapModal
          contact={postcodeMapContact}
          onClose={() => setPostcodeMapContact(null)}
        />
      )}
    </div>
  );
}

// Extract the sorted (alphabetical) franchisee dropdown list from the
// territory overlay payload, so the map filter dropdown labels line up
// with what's actually drawn beneath the pins.
function overlayFranchiseesFor(overlay) {
  const list = (overlay?.franchisees || []).map((f) => ({
    id: f.id,
    label: [
      [f.first_name, f.last_name].filter(Boolean).join(" "),
      f.organisation ? `(${f.organisation})` : "",
    ].filter(Boolean).join(" ").trim() || "(unnamed)",
  }));
  list.sort((a, b) => a.label.localeCompare(b.label));
  return list;
}

// Right-hand map panel for the Care Home Contacts tab. Filter bar on
// top (date preset + custom range + franchisee scope), map body below.
// Uses the same TerritoryMap component as Territory Builder so
// franchisee territories draw beneath the enquiry pins.
function CareHomeMapPanel({ filter, onFilterChange, pins, allPins, loading, error, overlay, franchisees, radius, onRadiusChange, radiusMode, onRadiusModeChange, hoveredContactId, onSelectContact }) {
  const set = (patch) => onFilterChange((prev) => ({ ...prev, ...patch }));
  const uncovered = pins.filter((p) => !p.franchisee_id).length;
  const covered = pins.length - uncovered;
  const handleMapClick = (lngLat) => {
    if (!radiusMode) return;
    onRadiusChange({ lat: lngLat.lat, lng: lngLat.lng, miles: radius?.miles || 5 });
    onRadiusModeChange(false);
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-3 sm:p-4 h-full flex flex-col" data-testid="care-home-map-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Care Home Enquiries · Map</div>
          <p className="text-xs text-stone-500 mt-1">Pins reflect the filters below — the list on the left mirrors them.</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="border border-stone-200 rounded-xl p-3 mb-3 space-y-2 bg-stone-50/40">
        <div className="flex items-center gap-1.5 flex-wrap">
          {[
            { p: "30d", label: "Last 30d" },
            { p: "12m", label: "Last 12m" },
            { p: "custom", label: "Custom…" },
            { p: "all", label: "All time" },
          ].map((o) => (
            <button
              key={o.p}
              onClick={() => set({ preset: o.p })}
              data-testid={`ch-filter-${o.p}`}
              className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md border ${
                filter.preset === o.p
                  ? "bg-stone-950 text-white border-stone-950"
                  : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {filter.preset === "custom" && (
          <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="ch-custom-range">
            <label className="flex items-center gap-1 text-stone-600">
              From
              <input type="date" value={filter.from} onChange={(e) => set({ from: e.target.value })} data-testid="ch-from"
                className="px-2 py-1 border border-stone-300 rounded text-xs" />
            </label>
            <label className="flex items-center gap-1 text-stone-600">
              To
              <input type="date" value={filter.to} onChange={(e) => set({ to: e.target.value })} data-testid="ch-to"
                className="px-2 py-1 border border-stone-300 rounded text-xs" />
            </label>
          </div>
        )}
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500">Franchisee territory</label>
          <select
            value={filter.franchiseeId || ""}
            onChange={(e) => set({ franchiseeId: e.target.value })}
            data-testid="ch-franchisee-picker"
            className="mt-1 w-full px-2 py-1.5 border border-stone-300 rounded text-xs bg-white"
          >
            <option value="">All territories</option>
            <option value="uncovered">Uncovered (outside every territory)</option>
            <option disabled>──────────</option>
            {franchisees.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </div>
        {/* Radius filter — click the map to drop a circle then use the
            slider to widen/narrow. Only pins inside the circle land
            in the list. */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onRadiusModeChange(!radiusMode)}
              data-testid="ch-radius-mode"
              className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md border flex items-center gap-1.5 ${
                radiusMode
                  ? "bg-amber-500 text-stone-950 border-amber-600 animate-pulse"
                  : radius ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
              }`}
            >
              {radius ? `Radius · ${radius.miles} mi` : "Draw radius"}
            </button>
            {radiusMode && (
              <span className="text-[10px] text-amber-800 italic">Click a point on the map…</span>
            )}
            {radius && (
              <>
                <input
                  type="range"
                  min="1" max="50" step="1"
                  value={radius.miles}
                  onChange={(e) => onRadiusChange({ ...radius, miles: parseInt(e.target.value, 10) || 5 })}
                  data-testid="ch-radius-slider"
                  className="flex-1 min-w-[100px] accent-stone-950"
                />
                <button
                  onClick={() => { onRadiusChange(null); onRadiusModeChange(false); }}
                  data-testid="ch-radius-clear"
                  className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-red-700 hover:text-red-900"
                >Clear</button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-2 text-[11px] text-stone-600">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-teal-600" /> Inside a territory ({covered})</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#93195a" }} /> Uncovered ({uncovered})</span>
        {radius && allPins && (
          <span className="text-stone-500">{pins.length} of {allPins.length} within radius</span>
        )}
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-400 ml-auto" />}
      </div>

      {error && <div className="text-sm text-red-700 mb-2">{error}</div>}

      <div className="flex-1 min-h-[500px]" data-testid="ch-map-container">
        <TerritoryMap
          sectors={[]}
          selected={[]}
          centreLabel=""
          height={620}
          interactive={false}
          franchiseeOverlay={overlay}
          pins={(allPins || pins).map((p) => ({
            id: p.id, lat: p.lat, lng: p.lng,
            color: p.id === hoveredContactId
              ? "#dddd16"
              : p.franchisee_id ? "#0d9488" : "#93195a",
            hovered: p.id === hoveredContactId,
            label: p.establishment_name || p.postcode,
          }))}
          radiusCircle={radius}
          onMapClick={handleMapClick}
          onPinClick={onSelectContact}
        />
      </div>
    </div>
  );
}

// Named re-exports so the pipeline Tabs view (and any future consumer)
// can reuse the same widgets instead of duplicating the checklist +
// notes save logic. Keeps the source of truth in this file.
export { InterestedChecklist, AdminNotesEditor, TemperaturePicker };

